import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { formatDate, formatDateTime, formatMoney } from '../core/format.js';
import { paymentMethodLabel } from '../core/paymentMethods.js';
import { icon, unitIcon } from '../core/icons.js';
import { t, getLocale } from '../core/i18n.js';
import { toast } from '../components/Toast.js';
import { showModal } from '../components/Modal.js';
import { attachMembersPanel } from '../components/MembersPanel.js';

const CHAT_POLL_MS = 10000;

const TENANCY_TABS = ['overview', 'agreements', 'meters', 'billing', 'handovers', 'maintenance', 'messages', 'members'];

export class TenancyView extends Component {
  constructor({ params, query } = { params: {} }) {
    super();
    this.id = params.id;
    const q = query || {};
    // Deep links: ?tab= opens a tab directly (dashboard maintenance rows),
    // ?action=amend opens the renewal form once the tenancy has loaded
    // (dashboard "expiring" rows) — consumed once, not kept in state.
    this._autoAction = q.action === 'amend' ? 'amend' : null;
    this.state = {
      tenancy: null,
      documents: null,
      payments: null,
      pendingItems: null,
      error: null,
      tab: TENANCY_TABS.includes(q.tab) ? q.tab : 'overview',
      meters: null,
      utilityFormOpen: false,
      utilityMeterId: '',
      utilityRate: '',
      utilityBaseFee: '',
      utilityDescription: '',
      meterReadingForms: {},
      pendingFormOpen: false,
      pendingForm: { type: 'fee', description: '', amount: '' },
      deposit: null,
      depositTxFormOpen: false,
      depositTxForm: { kind: 'RECEIVED', amount: '', note: '' },
      handovers: null,
      agreements: null,
      depositExpectedFormOpen: false,
      depositExpectedForm: { expected_amount: '' },
      amendOpen: false,
      amendStartDate: '',
      amendEndDate: '',
      amendDroppedUnits: {},
      amendAvailableUnits: null,
      amendAddedRents: {},
      amendKeptRents: {},
      amendBusy: false,
      editOpen: false,
      editForm: null,
      editDroppedUnits: {},
      editKeptRents: {},
      editAvailableUnits: null,
      editAddedRents: {},
      editBusy: false,
      tenantData: null,
      tenantApplyChecked: {},
      tenantApplyBusy: false,
      maintenance: null,
      maintenanceFormOpen: false,
      maintenanceBusy: false,
      maintenanceExpandedId: null,
      maintenanceDetail: null,
      maintenanceDetailBusy: false,
      msgUnread: 0,
    };
    this._photoUrls = {};
  }

  // Fetches an authenticated asset (image/PDF) once and caches the resulting
  // object URL keyed by `cacheKey` so re-renders don't re-download it.
  async _loadProtectedAsset(path, cacheKey) {
    if (this._photoUrls[cacheKey]) return this._photoUrls[cacheKey];
    try {
      const blob = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      this._photoUrls[cacheKey] = url;
      return url;
    } catch (_) {
      return null;
    }
  }

  _hydrateProtectedAssets() {
    this.el.querySelectorAll('a[data-protected-href]').forEach(async (a) => {
      const path = a.getAttribute('data-protected-href');
      const key = a.getAttribute('data-protected-key') || path;
      const url = await this._loadProtectedAsset(path, key);
      if (url) a.href = url;
    });
    this.el.querySelectorAll('img[data-protected-src]').forEach(async (img) => {
      const path = img.getAttribute('data-protected-src');
      const key = img.getAttribute('data-protected-key') || path;
      const url = await this._loadProtectedAsset(path, key);
      if (url) img.src = url;
    });
  }

  unmount() {
    this._stopChatPoll();
    for (const url of Object.values(this._photoUrls)) {
      try { URL.revokeObjectURL(url); } catch (_) {}
    }
    this._photoUrls = {};
  }

  // --- Tenancy chat ("messages" tab) ---------------------------------------
  // Imperative like InquiryView: the thread is cached on the instance and
  // painted into [data-chat-list], so polling and re-renders don't destroy
  // the input focus or scroll position.

  _messagesHtml() {
    return `
      <div class="card stack" data-chat>
        <div data-chat-list class="stack-2" style="max-height:420px;overflow-y:auto;padding:var(--space-2);background:var(--color-bg);border:1px solid var(--color-border);border-radius:var(--radius-md)"></div>
        <form class="row" style="gap:var(--space-2)" data-chat-form>
          <input class="input grow" type="text" data-chat-input maxlength="4000" placeholder="${esc(t('tenancy.messages.placeholder'))}" autocomplete="off" />
          <button class="btn btn-primary" type="submit" data-chat-send>
            ${icon('send', { size: 'sm' })}<span>${esc(t('tenancy.messages.send'))}</span>
          </button>
        </form>
      </div>
    `;
  }

  _wireChat() {
    const form = this.el.querySelector('[data-chat-form]');
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this._chatSend(); });
    if (!this._chatMessages) this._chatMessages = [];
    this._renderChatList(true);
    this._chatPoll(true);
    if (!this._chatTimer) this._chatTimer = setInterval(() => this._chatPoll(), CHAT_POLL_MS);
  }

  _stopChatPoll() {
    if (this._chatTimer) { clearInterval(this._chatTimer); this._chatTimer = null; }
  }

  async _chatPoll(initial = false) {
    if (document.hidden || this.state.tab !== 'messages') return;
    try {
      const msgs = this._chatMessages || (this._chatMessages = []);
      const last = msgs.length ? msgs[msgs.length - 1].id : null;
      const qs = last ? `?since=${encodeURIComponent(last)}` : '';
      const res = await api.get(`/tenancies/${this.id}/messages${qs}`);
      const items = Array.isArray(res && res.data) ? res.data : [];
      const seen = new Set(msgs.map((m) => m.id));
      let added = false;
      for (const m of items) {
        if (!m || !m.id || seen.has(m.id)) continue;
        msgs.push(m);
        added = true;
      }
      if (added || initial) this._renderChatList(initial);
      if (this.state.msgUnread) this.setState({ msgUnread: 0 });
    } catch (_) { /* poll errors are silent */ }
  }

  _renderChatList(force = false) {
    const list = this.el.querySelector('[data-chat-list]');
    if (!list) return;
    const msgs = this._chatMessages || [];
    const wasNearBottom = force || (list.scrollHeight - list.scrollTop - list.clientHeight) < 80;
    if (msgs.length === 0) {
      list.innerHTML = `<div class="empty">${esc(t('tenancy.messages.empty'))}</div>`;
      return;
    }
    list.innerHTML = msgs.map((m) => {
      const mine = !!m.mine;
      const who = mine ? t('tenancy.messages.you') : (m.sender_name || '');
      const bubbleStyle = mine
        ? 'background:var(--color-primary-soft);color:var(--color-primary);border:1px solid var(--color-primary-soft)'
        : 'background:var(--color-bg-card);color:var(--color-text);border:1px solid var(--color-border)';
      return `
        <div style="display:flex;flex-direction:column;align-items:${mine ? 'flex-end' : 'flex-start'};gap:2px">
          <div class="faint" style="font-size:0.75rem">${esc(who)} · ${esc(formatDateTime(m.created_at))}</div>
          <div style="max-width:80%;padding:6px 10px;border-radius:var(--radius-md);${bubbleStyle};white-space:pre-wrap;word-wrap:break-word">${esc(m.body)}</div>
        </div>`;
    }).join('');
    if (wasNearBottom) list.scrollTop = list.scrollHeight;
  }

  async _chatSend() {
    const input = this.el.querySelector('[data-chat-input]');
    const btn = this.el.querySelector('[data-chat-send]');
    if (!input) return;
    const body = (input.value || '').trim();
    if (!body) return;
    input.disabled = true;
    if (btn) btn.disabled = true;
    try {
      const msg = await api.post(`/tenancies/${this.id}/messages`, { body });
      input.value = '';
      if (msg && msg.id) {
        (this._chatMessages || (this._chatMessages = [])).push(msg);
        this._renderChatList(true);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('tenancy.messages.errors.send'));
    } finally {
      input.disabled = false;
      if (btn) btn.disabled = false;
      input.focus();
    }
  }

  template() {
    const c = this.state.tenancy;
    if (!c && !this.state.error) return `<div class="muted">${esc(t('tenancy.loading'))}</div>`;
    if (this.state.error && !c) return `<div class="error-banner">${esc(this.state.error)}</div>`;
    const isLandlord = auth.user && c && c.landlord_user_id === auth.user.id;
    const awaiting = !c.tenant_user_id && c.invitation_id;
    const tenantLabel = c.tenant_name || c.tenant_email || c.invitation_name || c.invitation_email || t('tenancy.tenant_fallback');
    const unitCount = Array.isArray(c.units) ? c.units.length : 0;
    return `
      <section class="stack">
        <a href="#/tenancies" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('tenancy.back'))}</span></a>
        <div class="panel" data-tenancy-header>
          <div class="panel-head">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('tenancy.title'))} · ${esc(tenantLabel)}</div>
              <h2 class="panel-title">${esc(formatMoney(c.monthly_rent_total, c.currency))} <span class="faint" style="font-size: 13px; text-transform: none; letter-spacing: 0; font-weight: 400;">${esc(t('tenancy.monthly_total'))}</span></h2>
            </div>
            <div class="panel-status">
              <span class="badge ${this._badgeClass(c.status)}">${esc(c.status)}</span>
            </div>
          </div>
          <div class="panel-body stack">
            <div class="tenancy-stats">
              <div class="tenancy-stat">
                <span class="tenancy-stat-k">${esc(t('tenancy.start') || 'Start')}</span>
                <strong>${esc(formatDate(c.start_date))}</strong>
              </div>
              <div class="tenancy-stat">
                <span class="tenancy-stat-k">${esc(t('tenancy.end') || 'End')}</span>
                <strong>${esc(c.end_date ? formatDate(c.end_date) : t('common.open'))}</strong>
              </div>
              <div class="tenancy-stat">
                <span class="tenancy-stat-k">${esc(t('tenancy.units_count') || 'Units')}</span>
                <strong>${unitCount}</strong>
              </div>
              <div class="tenancy-stat">
                <span class="tenancy-stat-k">${esc(t('tenancy.tenant_label') || 'Tenant')}</span>
                <strong>${esc(tenantLabel)}</strong>
              </div>
            </div>
            ${c.previous_tenancy_id || c.succeeded_by_tenancy_id ? `
              <div class="row" style="gap:var(--space-3); flex-wrap:wrap" data-amend-chain>
                ${c.previous_tenancy_id ? `<a href="#/tenancies/${esc(c.previous_tenancy_id)}" class="faint" data-chain-previous>${icon('arrow-left', { size: 'sm' })} ${esc(t('tenancy.chain.previous'))}</a>` : ''}
                ${c.succeeded_by_tenancy_id ? `<a href="#/tenancies/${esc(c.succeeded_by_tenancy_id)}" class="faint" data-chain-successor>${esc(t('tenancy.chain.successor'))} ${icon('arrow-right', { size: 'sm' })}</a>` : ''}
              </div>
            ` : ''}
            ${awaiting ? `<div class="warn-banner stack-2"><strong>${esc(t('tenancy.awaiting_strong'))}</strong><span class="faint">${esc(t('tenancy.awaiting_explain', { who: c.invitation_email || c.invitation_name || t('tenancy.the_tenant'), when: c.invitation_expires_at || '' }))}</span></div>` : ''}
            ${isLandlord && c.status === 'DRAFT' ? `
              <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
                <button class="btn btn-primary" data-action="activate">${icon('play-circle', { size: 'sm' })}<span>${esc(t('tenancy.activate'))}</span></button>
                <button class="btn btn-ghost" data-action="edit-open">${icon('pencil', { size: 'sm' })}<span>${esc(t('tenancy.edit_draft'))}</span></button>
                <button class="btn btn-ghost" data-action="delete-draft">${icon('trash', { size: 'sm' })}<span>${esc(t('tenancy.delete_draft'))}</span></button>
              </div>
            ` : ''}
            ${isLandlord && c.status === 'ACTIVE' ? `
              <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
                <button class="btn btn-ghost" data-action="amend-open">${icon('pencil', { size: 'sm' })}<span>${esc(t('tenancy.amend'))}</span></button>
                <button class="btn btn-ghost" data-action="end">${icon('stop-circle', { size: 'sm' })}<span>${esc(t('tenancy.end'))}</span></button>
              </div>
            ` : ''}
            ${isLandlord ? this._portalHtml(c) : ''}
            ${isLandlord && c.status === 'ACTIVE' && this.state.amendOpen ? this._amendFormHtml(c) : ''}
            ${isLandlord && c.status === 'DRAFT' && this.state.editOpen ? this._editFormHtml(c) : ''}
          </div>
        </div>

        <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
          <button class="btn ${this.state.tab === 'overview' ? 'btn-primary' : 'btn-ghost'}" data-tab="overview">${esc(t('tenancy.tabs.units'))}</button>
          <button class="btn ${this.state.tab === 'agreements' ? 'btn-primary' : 'btn-ghost'}" data-tab="agreements">${esc(t('tenancy.tabs.agreements'))}</button>
          <button class="btn ${this.state.tab === 'meters' ? 'btn-primary' : 'btn-ghost'}" data-tab="meters">${esc(t('tenancy.tabs.meters'))}</button>
          <button class="btn ${this.state.tab === 'billing' ? 'btn-primary' : 'btn-ghost'}" data-tab="billing">${esc(t('tenancy.tabs.billing'))}</button>
          <button class="btn ${this.state.tab === 'handovers' ? 'btn-primary' : 'btn-ghost'}" data-tab="handovers">${esc(t('tenancy.tabs.handovers'))}</button>
          <button class="btn ${this.state.tab === 'maintenance' ? 'btn-primary' : 'btn-ghost'}" data-tab="maintenance">${esc(t('tenancy.tabs.maintenance'))}</button>
          <button class="btn ${this.state.tab === 'messages' ? 'btn-primary' : 'btn-ghost'}" data-tab="messages">${esc(t('tenancy.tabs.messages'))}${this.state.msgUnread > 0 ? ` <span class="badge badge-danger">${this.state.msgUnread}</span>` : ''}</button>
          <button class="btn ${this.state.tab === 'members' ? 'btn-primary' : 'btn-ghost'}" data-tab="members">${esc(t('tenancy.tabs.members'))}</button>
        </div>

        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this.state.tab === 'overview' ? `${isLandlord ? this._tenantDataPanelHtml() : ''}${this._unitsHtml(c)}`
          : this.state.tab === 'agreements' ? this._agreementsHtml(c, isLandlord)
          : this.state.tab === 'meters' ? this._metersHtml(c, isLandlord)
          : this.state.tab === 'handovers' ? this._handoversHtml(c, isLandlord)
          : this.state.tab === 'maintenance' ? this._maintenanceHtml(c, isLandlord)
          : this.state.tab === 'messages' ? this._messagesHtml()
          : this.state.tab === 'members' ? `<div data-members-slot></div>`
          : this._billingHtml(c, isLandlord)}
      </section>
    `;
  }

  _amendFormHtml(c) {
    const today = new Date().toISOString().slice(0, 10);
    const minStart = c.start_date && c.start_date >= today ? c.start_date : today;
    const start = this.state.amendStartDate || minStart;
    const dropped = this.state.amendDroppedUnits || {};
    const added = this.state.amendAddedRents || {};
    const kept = (c.units || []).filter((u) => !dropped[u.unit_id]);
    const totalUnits = kept.length + Object.keys(added).length;
    return `
      <form class="stack" data-amend-form novalidate>
        <div class="muted">
          ${esc(t('tenancy.amend_form.explain'))}
        </div>
        <label class="stack-2">
          <span class="faint">${esc(t('tenancy.amend_form.new_start'))}</span>
          <input class="input" type="date" name="start_date" required value="${esc(start)}" min="${esc(minStart)}" />
        </label>
        <label class="stack-2">
          <span class="faint">${esc(t('tenancy.amend_form.new_end'))}</span>
          <input class="input" type="date" name="end_date" value="${esc(this.state.amendEndDate || '')}" min="${esc(start)}" />
          <span class="faint">${esc(t('tenancy.amend_form.new_end_hint'))}</span>
        </label>
        <div class="stack-2">
          <span class="faint">${esc(t('tenancy.amend_form.carry_units'))}</span>
          ${(c.units || []).map((u) => {
            const isKept = !dropped[u.unit_id];
            const rentVal = Object.prototype.hasOwnProperty.call(this.state.amendKeptRents, u.unit_id)
              ? this.state.amendKeptRents[u.unit_id]
              : String(Math.round((u.rent_amount || 0) / 100));
            return `
            <div class="stack-2">
              <label class="row" style="gap:var(--space-3)">
                <input type="checkbox" data-amend-unit="${esc(u.unit_id)}" ${isKept ? 'checked' : ''} />
                <span>${esc(u.identifier)} <span class="faint">· ${esc(u.type)}</span></span>
              </label>
              ${isKept ? `
                <label class="stack-2" style="margin-left:var(--space-5)">
                  <span class="faint">${esc(t('tenancy.amend_form.kept_rent_label'))} (${esc(c.currency)})</span>
                  <input class="input" type="number" min="0" step="1" data-amend-kept-rent="${esc(u.unit_id)}" value="${esc(rentVal)}" />
                </label>
              ` : ''}
            </div>
          `;
          }).join('')}
        </div>
        ${this._amendAddUnitsHtml(c)}
        <div class="row" style="gap:var(--space-2)">
          <button class="btn btn-primary" type="submit" ${this.state.amendBusy || totalUnits === 0 ? 'disabled' : ''}>
            ${icon('check', { size: 'sm' })}<span>${esc(t('tenancy.amend_form.submit'))}</span>
          </button>
          <button class="btn btn-ghost" type="button" data-action="amend-cancel" ${this.state.amendBusy ? 'disabled' : ''}>${esc(t('common.cancel'))}</button>
        </div>
      </form>
    `;
  }

  _amendAddUnitsHtml(c) {
    const list = this.state.amendAvailableUnits;
    if (list === null) {
      return `<div class="stack-2"><span class="faint">${esc(t('tenancy.amend_form.add_units'))}</span><div class="muted">${esc(t('tenancy.amend_form.add_units_loading'))}</div></div>`;
    }
    if (list.length === 0) {
      return `<div class="stack-2"><span class="faint">${esc(t('tenancy.amend_form.add_units'))}</span><div class="muted">${esc(t('tenancy.amend_form.add_units_none'))}</div></div>`;
    }
    const added = this.state.amendAddedRents || {};
    return `
      <div class="stack-2">
        <span class="faint">${esc(t('tenancy.amend_form.add_units'))}</span>
        ${list.map((u) => {
          const checked = Object.prototype.hasOwnProperty.call(added, u.id);
          const blocked = u.availability_status === 'occupied' && u.current_tenancy_open_ended;
          const occupiedLabel = u.availability_status === 'occupied'
            ? (u.current_tenancy_open_ended
              ? t('tenancy.amend_form.occupied_open_ended')
              : t('tenancy.amend_form.occupied_until', { date: u.current_tenancy_end_date || '' }))
            : '';
          return `
            <div class="stack-2">
              <label class="row" style="gap:var(--space-3)">
                <input type="checkbox" data-amend-add-unit="${esc(u.id)}" ${checked ? 'checked' : ''} ${blocked ? 'disabled' : ''} />
                <span>${esc(u.identifier)} <span class="faint">· ${esc(u.type)}${u.property_name ? ' · ' + esc(u.property_name) : ''}${occupiedLabel ? ' · ' + esc(occupiedLabel) : ''}</span></span>
              </label>
              ${checked ? `
                <label class="stack-2" style="margin-left:var(--space-5)">
                  <span class="faint">${esc(t('tenancy.amend_form.add_unit_rent_label'))} (${esc(c.currency)})</span>
                  <input class="input" type="number" min="0" step="1" data-amend-add-rent="${esc(u.id)}" value="${esc(added[u.id] ?? '')}" />
                </label>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  _editFormHtml(c) {
    const f = this.state.editForm || {};
    const dropped = this.state.editDroppedUnits || {};
    const added = this.state.editAddedRents || {};
    const kept = (c.units || []).filter((u) => !dropped[u.unit_id]);
    const totalUnits = kept.length + Object.keys(added).length;
    return `
      <form class="stack" data-edit-form novalidate>
        <div class="muted">${esc(t('tenancy.edit_form.explain'))}</div>
        <div class="row" style="gap:var(--space-3); flex-wrap:wrap">
          <label class="stack-2 grow">
            <span class="faint">${esc(t('tenancy.edit_form.start_date'))}</span>
            <input class="input" type="date" name="start_date" value="${esc(f.start_date || '')}" required />
          </label>
          <label class="stack-2 grow">
            <span class="faint">${esc(t('tenancy.edit_form.end_date'))}</span>
            <input class="input" type="date" name="end_date" value="${esc(f.end_date || '')}" />
          </label>
          <label class="stack-2 grow">
            <span class="faint">${esc(t('tenancy.edit_form.currency'))}</span>
            <input class="input" name="currency" maxlength="3" value="${esc(f.currency || '')}" required />
          </label>
        </div>
        <label class="stack-2">
          <span class="faint">${esc(t('tenancy.edit_form.deposit_amount'))} (${esc(f.currency || c.currency)})</span>
          <input class="input" type="number" min="0" step="1" name="deposit_amount" value="${esc(f.deposit_amount ?? '')}" />
        </label>
        <label class="stack-2">
          <span class="faint">${esc(t('tenancy.edit_form.notes'))}</span>
          <textarea class="input" name="notes" rows="2">${esc(f.notes || '')}</textarea>
        </label>
        <div class="stack-2">
          <span class="faint">${esc(t('tenancy.edit_form.units'))}</span>
          ${(c.units || []).map((u) => {
            const isKept = !dropped[u.unit_id];
            const rentVal = Object.prototype.hasOwnProperty.call(this.state.editKeptRents, u.unit_id)
              ? this.state.editKeptRents[u.unit_id]
              : String(Math.round((u.rent_amount || 0) / 100));
            return `
            <div class="stack-2">
              <label class="row" style="gap:var(--space-3)">
                <input type="checkbox" data-edit-unit="${esc(u.unit_id)}" ${isKept ? 'checked' : ''} />
                <span>${esc(u.identifier)} <span class="faint">· ${esc(u.type)}</span></span>
              </label>
              ${isKept ? `
                <label class="stack-2" style="margin-left:var(--space-5)">
                  <span class="faint">${esc(t('tenancy.edit_form.rent_label'))} (${esc(f.currency || c.currency)})</span>
                  <input class="input" type="number" min="0" step="1" data-edit-kept-rent="${esc(u.unit_id)}" value="${esc(rentVal)}" />
                </label>
              ` : ''}
            </div>
          `;
          }).join('')}
        </div>
        ${this._editAddUnitsHtml(c)}
        <div class="row" style="gap:var(--space-2)">
          <button class="btn btn-primary" type="submit" ${this.state.editBusy || totalUnits === 0 ? 'disabled' : ''}>
            ${icon('check', { size: 'sm' })}<span>${esc(t('tenancy.edit_form.submit'))}</span>
          </button>
          <button class="btn btn-ghost" type="button" data-action="edit-cancel" ${this.state.editBusy ? 'disabled' : ''}>${esc(t('common.cancel'))}</button>
        </div>
      </form>
    `;
  }

  _editAddUnitsHtml(c) {
    const list = this.state.editAvailableUnits;
    if (list === null) {
      return `<div class="stack-2"><span class="faint">${esc(t('tenancy.edit_form.add_units'))}</span><div class="muted">${esc(t('tenancy.edit_form.add_units_loading'))}</div></div>`;
    }
    if (list.length === 0) {
      return `<div class="stack-2"><span class="faint">${esc(t('tenancy.edit_form.add_units'))}</span><div class="muted">${esc(t('tenancy.edit_form.add_units_none'))}</div></div>`;
    }
    const added = this.state.editAddedRents || {};
    const cur = (this.state.editForm && this.state.editForm.currency) || c.currency;
    return `
      <div class="stack-2">
        <span class="faint">${esc(t('tenancy.edit_form.add_units'))}</span>
        ${list.map((u) => {
          const checked = Object.prototype.hasOwnProperty.call(added, u.id);
          const blocked = u.availability_status === 'occupied' && u.current_tenancy_open_ended;
          const occupiedLabel = u.availability_status === 'occupied'
            ? (u.current_tenancy_open_ended
              ? t('tenancy.amend_form.occupied_open_ended')
              : t('tenancy.amend_form.occupied_until', { date: u.current_tenancy_end_date || '' }))
            : '';
          return `
            <div class="stack-2">
              <label class="row" style="gap:var(--space-3)">
                <input type="checkbox" data-edit-add-unit="${esc(u.id)}" ${checked ? 'checked' : ''} ${blocked ? 'disabled' : ''} />
                <span>${esc(u.identifier)} <span class="faint">· ${esc(u.type)}${u.property_name ? ' · ' + esc(u.property_name) : ''}${occupiedLabel ? ' · ' + esc(occupiedLabel) : ''}</span></span>
              </label>
              ${checked ? `
                <label class="stack-2" style="margin-left:var(--space-5)">
                  <span class="faint">${esc(t('tenancy.edit_form.rent_label'))} (${esc(cur)})</span>
                  <input class="input" type="number" min="0" step="1" data-edit-add-rent="${esc(u.id)}" value="${esc(added[u.id] ?? '')}" />
                </label>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  }


  // Card listing the values our OCR pulled out of approved KYC items, with
  // checkboxes to push the selected fields onto the tenant's profile.
  // Renders nothing until the data has been fetched and at least one
  // proposed field exists; loading/empty states stay invisible to keep the
  // overview tab clean for tenancies without document requests.
  _tenantDataPanelHtml() {
    const td = this.state.tenantData;
    if (!td) return '';
    const proposed = td.proposed || {};
    const keys = Object.keys(proposed);
    if (keys.length === 0) return '';
    const current = td.current || {};
    const checked = this.state.tenantApplyChecked || {};
    const fieldRow = (key) => {
      const cur = current[key] || '';
      const next = proposed[key] || '';
      const same = cur.trim() === next.trim();
      const isChecked = checked[key] !== undefined ? checked[key] : !same;
      return `
        <label class="row" style="gap:var(--space-3); align-items:flex-start">
          <input type="checkbox" data-tenant-apply-field="${esc(key)}" ${isChecked ? 'checked' : ''} ${same ? 'disabled' : ''} />
          <div class="stack-2 grow">
            <strong>${esc(t('tenancy.tenant_data.fields.' + key))}</strong>
            <div class="faint" style="white-space:pre-wrap">
              ${cur ? `<span>${esc(t('tenancy.tenant_data.current'))}<span class="muted">${esc(cur)}</span></span><br/>` : ''}
              <span>${esc(t('tenancy.tenant_data.proposed'))}<strong>${esc(next)}</strong></span>
              ${same ? `<br/><span class="faint">${esc(t('tenancy.tenant_data.unchanged'))}</span>` : ''}
            </div>
          </div>
        </label>
      `;
    };
    const itemSummary = (td.items || []).map((it) => {
      const kindLabel = t('tenant_requests.kinds.' + it.kind);
      const fieldNames = Object.keys(it.fields).join(', ');
      return `<li><span class="faint">${esc(kindLabel)}:</span> ${esc(fieldNames)}</li>`;
    }).join('');
    const anyChecked = keys.some((k) => {
      if (checked[k] !== undefined) return checked[k];
      return (current[k] || '').trim() !== (proposed[k] || '').trim();
    });
    return `
      <div class="card stack" data-tenant-data-panel>
        <div class="row-between">
          <strong>${esc(t('tenancy.tenant_data.title'))}</strong>
          <span class="faint">${esc(t('tenancy.tenant_data.from_count', { count: (td.items || []).length }))}</span>
        </div>
        <div class="muted">${esc(t('tenancy.tenant_data.explain'))}</div>
        <div class="stack-2">${keys.map(fieldRow).join('')}</div>
        ${itemSummary ? `<details><summary class="faint">${esc(t('tenancy.tenant_data.sources'))}</summary><ul>${itemSummary}</ul></details>` : ''}
        <div class="row" style="gap:var(--space-2)">
          <button class="btn btn-primary" data-action="tenant-data-apply" ${this.state.tenantApplyBusy || !anyChecked ? 'disabled' : ''}>
            ${icon('check', { size: 'sm' })}<span>${esc(this.state.tenantApplyBusy ? t('tenancy.tenant_data.applying') : t('tenancy.tenant_data.apply'))}</span>
          </button>
        </div>
      </div>
    `;
  }

  _unitsHtml(c) {
    if (!c.units || c.units.length === 0) return `<div class="empty">${esc(t('tenancy.units.empty'))}</div>`;
    return `<div class="list">${c.units.map((u) => `
      <div class="list-item">
        ${icon(unitIcon(u.type), { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(u.identifier)} <span class="faint">· ${esc(u.type)}</span></strong>
          <span class="faint">${esc(u.property_name || '')} — ${esc(u.address_line1 || '')}</span>
        </div>
        <span>${esc(formatMoney(u.rent_amount, c.currency))}</span>
      </div>
    `).join('')}</div>`;
  }

  _metersHtml(c, isLandlord) {
    const list = this.state.meters;
    if (list === null) return `<div class="muted">${esc(t('tenancy.meters.loading'))}</div>`;
    if (list.length === 0) {
      return isLandlord
        ? `<div class="empty">${esc(t('tenancy.meters.empty_landlord'))}</div>`
        : `<div class="empty">${esc(t('tenancy.meters.empty_tenant'))}</div>`;
    }
    return `<div class="stack">${list.map((m) => this._meterCardHtml(m, c, isLandlord)).join('')}</div>`;
  }

  _meterCardHtml(m, c, isLandlord) {
    const readings = Array.isArray(m.readings) ? m.readings : [];
    const form = this.state.meterReadingForms[m.id] || null;
    const tag = m.label ? `${m.kind} · ${m.label}` : m.kind;
    const dueHint = m.reading_day_of_month
      ? `<span class="faint">${esc(t('tenancy.meters.due_day', { day: m.reading_day_of_month }))}</span>`
      : '';
    const dueBadge = this._meterDueBadgeHtml(m, readings);
    return `
      <div class="card stack" data-meter-card="${esc(m.id)}">
        <div class="row-between">
          <div class="stack-2">
            <strong>${esc(m.unit_label)} <span class="faint">· ${esc(tag)}</span></strong>
            <span class="faint">${esc(m.unit_of_measure)}${m.serial_number ? ' · #' + esc(m.serial_number) : ''}</span>
            <div class="row" style="gap:var(--space-2);align-items:center">${dueHint}${dueBadge}</div>
          </div>
          ${form ? '' : `<button class="btn btn-ghost" data-action="reading-open" data-meter-id="${esc(m.id)}">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.meters.add_reading'))}</span></button>`}
        </div>
        ${readings.length === 0
          ? `<div class="empty">${esc(t('tenancy.meters.no_readings'))}</div>`
          : `<div class="stack-2">${readings.map((r) => this._readingRowHtml(m, r, isLandlord)).join('')}</div>`}
        ${form ? this._readingFormHtml(m, form) : ''}
      </div>
    `;
  }

  // Mirror TasksService::buildTenantTasks: surface a colored badge when a
  // reading is due within 5 days (or already overdue) and no reading has been
  // submitted inside that same window. Fee meters and meters without a
  // configured due day are skipped.
  _meterDueBadgeHtml(m, readings) {
    const status = this._meterDueStatus(m, readings);
    if (status === null) return '';
    const cls = status.kind === 'overdue' ? 'badge badge-danger'
      : status.kind === 'due_today' ? 'badge badge-warning'
      : 'badge badge-warning';
    let label;
    if (status.kind === 'overdue') {
      const n = Math.abs(status.days);
      label = t(n === 1 ? 'tasks.tenant.overdue_one' : 'tasks.tenant.overdue_other', { count: n });
    } else if (status.kind === 'due_today') {
      label = t('tasks.tenant.due_today');
    } else {
      const n = status.days;
      label = t(n === 1 ? 'tasks.tenant.due_in_days_one' : 'tasks.tenant.due_in_days_other', { count: n });
    }
    return `<span class="${cls}">${esc(label)}</span>`;
  }

  _meterDueStatus(m, readings) {
    const dueDay = Number(m.reading_day_of_month);
    if (!dueDay || (m.kind || '').toLowerCase() === 'fee') return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextDue = this._nextDueDate(today, dueDay);
    const days = Math.round((nextDue.getTime() - today.getTime()) / 86400000);
    if (days > 5) return null;
    const windowStart = new Date(nextDue.getTime() - 5 * 86400000);
    const windowStartIso = windowStart.toISOString().slice(0, 10);
    const submitted = readings.some((r) => String(r.read_at || '') >= windowStartIso);
    if (submitted) return null;
    return {
      kind: days < 0 ? 'overdue' : (days === 0 ? 'due_today' : 'due_soon'),
      days,
    };
  }

  _nextDueDate(today, dueDay) {
    const y = today.getFullYear();
    const m = today.getMonth();
    const thisMonth = this._clampToMonth(y, m, dueDay);
    if (thisMonth.getTime() >= today.getTime()) return thisMonth;
    return this._clampToMonth(y, m + 1, dueDay);
  }

  _clampToMonth(year, month, day) {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(day, lastDay));
  }


  _readingRowHtml(m, r, isLandlord) {
    const status = String(r.status || 'ACCEPTED').toUpperCase();
    const badgeCls = status === 'ACCEPTED' ? 'badge-success'
      : status === 'REJECTED' ? 'badge-danger' : 'badge-warning';
    const delta = r.previous_value !== null && r.previous_value !== undefined
      ? ` <span class="faint">${esc(t('tenancy.meters.delta', { delta: r.delta, uom: m.unit_of_measure }))}</span>` : '';
    const photoLink = r.photo_path
      ? ` · <a href="#" target="_blank" rel="noopener" data-protected-href="/readings/${esc(r.id)}/photo" data-protected-key="reading:${esc(r.id)}">${esc(t('tenancy.meters.photo_link'))}</a>`
      : '';
    const note = r.review_note
      ? `<div class="faint">${esc(t('tenancy.meters.note_label', { note: r.review_note }))}</div>` : '';
    const reviewBtns = isLandlord && status === 'PENDING'
      ? `<button class="btn btn-ghost" data-action="reading-accept" data-reading-id="${esc(r.id)}">${esc(t('tenancy.meters.accept'))}</button>
         <button class="btn btn-ghost" data-action="reading-reject" data-reading-id="${esc(r.id)}">${esc(t('tenancy.meters.reject'))}</button>`
      : '';
    const ownReading = auth.user && r.created_by_user_id === auth.user.id;
    const tenantCanDelete = !isLandlord && ownReading && (status === 'PENDING' || status === 'REJECTED');
    const canDelete = isLandlord || tenantCanDelete;
    const deleteBtn = canDelete
      ? `<button class="btn btn-ghost btn-icon" data-action="reading-delete" data-reading-id="${esc(r.id)}" aria-label="${esc(t('tenancy.meters.delete_aria'))}">${icon('trash', { size: 'sm' })}</button>`
      : '';
    // A sent-back reading isn't counted; the creator can resubmit it for
    // review just by attaching a photo (no re-typing the value).
    const canResubmit = !isLandlord && ownReading && status === 'REJECTED';
    const resubmitHint = canResubmit
      ? `<div class="faint">${esc(t('tenancy.meters.rejected_hint'))}</div>` : '';
    const resubmitBtn = canResubmit
      ? `<button class="btn btn-ghost" data-action="reading-resubmit" data-reading-id="${esc(r.id)}">${icon('camera', { size: 'sm' })}<span>${esc(t('tenancy.meters.resubmit_photo'))}</span></button>`
      : '';
    return `
      <div class="row-between" data-reading-row="${esc(r.id)}">
        <div class="stack-2">
          <div><strong>${esc(r.reading_value)} ${esc(m.unit_of_measure)}</strong>${delta} <span class="faint">· ${esc(r.read_at)}</span>${photoLink}</div>
          <div class="row" style="gap:var(--space-2)"><span class="badge ${badgeCls}">${esc(status)}</span>${note}</div>
          ${resubmitHint}
        </div>
        <div class="row" style="gap:var(--space-2)">${reviewBtns}${resubmitBtn}${deleteBtn}</div>
      </div>
    `;
  }

  _readingFormHtml(m, form) {
    const sameDay = (m.readings || []).some((r) => r.read_at === form.read_at);
    const sameDayWarning = sameDay
      ? `<div class="faint" data-reading-sameday-warning>${esc(t('tenancy.meters.form.same_day_warning', { date: form.read_at }))}</div>`
      : '';
    return `
      <form class="stack" data-reading-form="${esc(m.id)}" enctype="multipart/form-data">
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('tenancy.meters.form.reading_label', { uom: m.unit_of_measure }))}</label>
            <input class="input" name="reading_value" type="number" step="0.001" min="0" value="${esc(form.value)}" required />
          </div>
          <div class="field grow">
            <label>${esc(t('tenancy.meters.form.date'))}</label>
            <input class="input" name="read_at" type="date" value="${esc(form.read_at)}" required />
          </div>
        </div>
        ${sameDayWarning}
        <div class="field">
          <label>${esc(t('tenancy.meters.form.photo'))}</label>
          <input class="input" name="photo" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" />
        </div>
        <div class="row" style="justify-content:flex-end;gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="reading-cancel" data-meter-id="${esc(m.id)}">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${form.busy ? 'disabled' : ''}>${form.busy ? '…' : esc(t('tenancy.meters.form.submit'))}</button>
        </div>
      </form>
    `;
  }

  _handoversHtml(c, isLandlord) {
    const list = this.state.handovers;
    if (list === null) return `<div class="muted">${esc(t('tenancy.handovers.loading'))}</div>`;
    const canCreate = isLandlord && (c.status === 'DRAFT' || c.status === 'ACTIVE');
    const createBtns = canCreate ? `
      <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
        <button class="btn btn-primary" data-action="handover-create" data-type="MOVE_IN">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.handovers.create_movein'))}</span></button>
        <button class="btn btn-ghost" data-action="handover-create" data-type="MOVE_OUT">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.handovers.create_moveout'))}</span></button>
        <button class="btn btn-ghost" data-action="handover-create" data-type="EVENT">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.handovers.create_event'))}</span></button>
      </div>` : '';
    const items = list.length === 0
      ? `<div class="empty">${esc(t('tenancy.handovers.empty'))}</div>`
      : `<div class="list">${list.map((h) => this._handoverRowHtml(h)).join('')}</div>`;
    return `<div class="stack">${createBtns}${items}</div>`;
  }

  _handoverRowHtml(h) {
    const typeLabel = t('tenancy.handovers.type.' + (h.type || 'MOVE_IN'));
    const statusLabel = t('tenancy.handovers.status.' + (h.status || 'DRAFT'));
    const when = h.completed_at ? formatDateTime(h.completed_at) : formatDateTime(h.created_at);
    return `
      <div class="list-item handover-list-item">
        <a class="handover-list-link" href="#/handovers/${esc(h.id)}">
          ${icon('paperclip', { class: 'list-leading icon-faint' })}
          <div class="stack-2 grow">
            <strong>${esc(typeLabel)} <span class="faint">· ${esc(statusLabel)}</span></strong>
            <span class="faint">${esc(when)}</span>
          </div>
        </a>
        <button class="btn btn-ghost btn-icon" data-action="handover-pdf" data-id="${esc(h.id)}" aria-label="${esc(t('handover.download_pdf'))}" title="${esc(t('handover.download_pdf'))}">${icon('download', { size: 'sm' })}</button>
      </div>
    `;
  }

  /** Rental agreement PDFs (TEMPLATE-rendered, CUSTOM-assembled, or
   *  manager-uploaded) for the tenancy. The list is loaded lazily when
   *  the tab is opened; each row exposes a download button hitting
   *  /tenancy-agreements/{id}/pdf. The TRACK_ONLY upload affordance only
   *  surfaces for landlords on tenancies that were created via the
   *  TRACK_ONLY path (state.tenancy.source === 'TRACK_ONLY'). */
  _agreementsHtml(c, isLandlord) {
    const list = this.state.agreements;
    if (list === null) return `<div class="muted">${esc(t('tenancy.agreements.loading'))}</div>`;
    const showUpload = isLandlord && (c.source || 'TRACK_ONLY') === 'TRACK_ONLY';
    const upload = showUpload ? `
      <div class="card stack-2">
        <div class="row-between">
          <strong>${esc(t('tenancy.agreements.upload_title'))}</strong>
          <span class="faint">${esc(t('tenancy.agreements.upload_hint'))}</span>
        </div>
        <form class="row" style="gap:var(--space-2);align-items:center" data-agreement-upload>
          <input type="file" name="pdf" accept="application/pdf" data-agreement-file required />
          <input type="text" class="input" name="title" placeholder="${esc(t('tenancy.agreements.upload_title_placeholder'))}" data-agreement-title />
          <button type="submit" class="btn btn-primary">${icon('upload', { size: 'sm' })}<span>${esc(t('tenancy.agreements.upload_submit'))}</span></button>
        </form>
        <div data-agreement-upload-error></div>
      </div>
    ` : '';
    const items = list.length === 0
      ? `<div class="empty">${esc(t('tenancy.agreements.empty'))}</div>`
      : `<div class="list">${list.map((a) => this._agreementRowHtml(a)).join('')}</div>`;
    return `<div class="stack">${upload}${items}</div>`;
  }

  _agreementRowHtml(a) {
    const sourceBadge = a.source === 'TEMPLATE' ? 'badge-success'
      : a.source === 'CUSTOM' ? 'badge-warning'
      : 'badge-muted';
    const sourceLabel = t('tenancy.agreements.source.' + (a.source || 'UPLOAD'));
    const size = (Number(a.byte_size || 0) / 1024).toFixed(1) + ' KB';
    return `
      <div class="list-item">
        ${icon('file-text', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(a.title || a.file_name)} <span class="badge ${sourceBadge}">${esc(sourceLabel)}</span></strong>
          <span class="faint">${esc(formatDateTime(a.created_at))} · ${esc(size)}</span>
        </div>
        <button type="button" class="btn btn-ghost btn-icon" data-action="agreement-pdf" data-id="${esc(a.id)}" data-name="${esc(a.file_name || (a.title || 'agreement') + '.pdf')}" aria-label="${esc(t('tenancy.agreements.download'))}" title="${esc(t('tenancy.agreements.download'))}">${icon('download', { size: 'sm' })}</button>
      </div>
    `;
  }

  _billingHtml(c, isLandlord) {
    const docs = this.state.documents;
    const payments = this.state.payments;
    const pending = this.state.pendingItems;
    const proformaLabel = pending && pending.length > 0
      ? t('tenancy.billing.new_proforma_with_pending', { count: pending.length })
      : t('tenancy.billing.new_proforma_default');
    return `
      <div class="stack">
        ${isLandlord && c.status === 'ACTIVE' ? `
          <button class="btn btn-primary" data-action="proforma">${icon('receipt', { size: 'sm' })}<span>${esc(proformaLabel)}</span></button>
          <button class="btn btn-ghost" data-action="utility-open">${icon('receipt', { size: 'sm' })}<span>${esc(t('tenancy.billing.new_utility'))}</span></button>
          <button class="btn btn-ghost" data-action="payment">${icon('credit-card', { size: 'sm' })}<span>${esc(t('tenancy.billing.record_payment'))}</span></button>
        ` : ''}
        ${this.state.utilityFormOpen ? this._utilityFormHtml(c) : ''}
        ${this._depositHtml(c, isLandlord)}
        ${this._pendingHtml(c, isLandlord)}
        <h3>${esc(t('tenancy.billing.documents'))}</h3>
        ${docs === null ? `<div class="muted">${esc(t('tenancy.billing.loading'))}</div>`
          : docs.length === 0 ? `<div class="empty">${esc(t('tenancy.billing.no_documents'))}</div>`
          : `<div class="list">${docs.map((d) => this._docHtml(d, c)).join('')}</div>`}
        <h3>${esc(t('tenancy.billing.payments'))}</h3>
        ${payments === null ? `<div class="muted">${esc(t('tenancy.billing.loading'))}</div>`
          : payments.length === 0 ? `<div class="empty">${esc(t('tenancy.billing.no_payments'))}</div>`
          : `<div class="list">${payments.map((p) => `
            <div class="list-item">
              ${icon('credit-card', { class: 'list-leading icon-faint' })}
              <div class="stack-2 grow">
                <strong>${esc(formatMoney(p.amount, p.currency))}</strong>
                <span class="faint">${esc(paymentMethodLabel(p.method))} · ${esc(formatDateTime(p.paid_at))}${p.reference ? ' · ' + esc(p.reference) : ''}</span>
              </div>
            </div>
          `).join('')}</div>`}
      </div>
    `;
  }

  _pendingHtml(c, isLandlord) {
    const items = this.state.pendingItems;
    if (items === null) return '';
    if (items.length === 0 && !isLandlord) return '';
    const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    return `
      <div class="card stack">
        <div class="row-between">
          <h3 style="margin:0">${esc(t('tenancy.billing.pending.title'))}</h3>
          ${isLandlord && c.status === 'ACTIVE' && !this.state.pendingFormOpen
            ? `<button class="btn btn-ghost" data-action="pending-open">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.billing.pending.add'))}</span></button>`
            : ''}
        </div>
        <p class="muted" style="margin:0">${esc(t('tenancy.billing.pending.explain'))}</p>
        ${items.length === 0
          ? `<div class="empty">${esc(t('tenancy.billing.pending.none'))}</div>`
          : `<div class="list">${items.map((it) => `
            <div class="list-item">
              ${icon('plus', { class: 'list-leading icon-faint' })}
              <div class="stack-2 grow">
                <strong>${esc(it.description)}</strong>
                <span class="faint">${esc(it.type)}${it.notes ? ' · ' + esc(it.notes) : ''}</span>
              </div>
              <div class="stack-2" style="text-align:right">
                <strong>${esc(formatMoney(Number(it.amount), c.currency))}</strong>
                ${isLandlord && c.status === 'ACTIVE'
                  ? `<button class="btn btn-ghost" data-action="pending-cancel" data-id="${esc(it.id)}">${esc(t('common.remove'))}</button>`
                  : ''}
              </div>
            </div>
          `).join('')}<div class="row-between" style="padding:var(--space-2) var(--space-3)">
              <span class="faint">${esc(t('tenancy.billing.pending.total'))}</span>
              <strong>${esc(formatMoney(total, c.currency))}</strong>
            </div></div>`}
        ${this.state.pendingFormOpen ? this._pendingFormHtml(c) : ''}
      </div>
    `;
  }

  _depositHtml(c, isLandlord) {
    const d = this.state.deposit;
    if (d === null) return '';
    const showCard = isLandlord || d.expected_amount > 0 || d.held_balance > 0 || (d.transactions || []).length > 0;
    if (!showCard) return '';
    const currency = d.currency || c.currency;
    const status = d.status || 'NONE';
    const canEditExpected = isLandlord && c.status !== 'ENDED' && (status === 'NONE' || status === 'EXPECTED' || status === 'PARTIAL');
    const canRecord = isLandlord && c.status !== 'ENDED';
    return `
      <div class="card stack">
        <div class="row-between">
          <h3 style="margin:0">${esc(t('tenancy.billing.deposit.title'))}</h3>
          <span class="badge ${esc(this._depositBadgeClass(status))}">${esc(t('tenancy.billing.deposit.status.' + status))}</span>
        </div>
        <div class="row-between">
          <span class="faint">${esc(t('tenancy.billing.deposit.expected'))}</span>
          <strong>${esc(formatMoney(d.expected_amount, currency))}</strong>
        </div>
        <div class="row-between">
          <span class="faint">${esc(t('tenancy.billing.deposit.held'))}</span>
          <strong>${esc(formatMoney(d.held_balance, currency))}</strong>
        </div>
        ${d.received_at ? `<div class="row-between"><span class="faint">${esc(t('tenancy.billing.deposit.received_at'))}</span><span>${esc(formatDate(d.received_at))}</span></div>` : ''}
        ${d.settled_at ? `<div class="row-between"><span class="faint">${esc(t('tenancy.billing.deposit.settled_at'))}</span><span>${esc(formatDate(d.settled_at))}</span></div>` : ''}
        ${d.settlement_note ? `<div class="muted">${esc(d.settlement_note)}</div>` : ''}
        ${(d.transactions || []).length === 0 ? '' : `
          <details class="list-item-details">
            <summary class="list-item"><span class="grow">${esc(t('tenancy.billing.deposit.transactions'))} (${(d.transactions || []).length})</span></summary>
            <div class="list">${(d.transactions || []).map((tx) => `
              <div class="list-item">
                <div class="stack-2 grow">
                  <strong>${esc(t('tenancy.billing.deposit.kinds.' + tx.kind))}</strong>
                  <span class="faint">${esc(formatDate(tx.occurred_on))}${tx.note ? ' · ' + esc(tx.note) : ''}</span>
                </div>
                <strong style="text-align:right">${esc(this._depositTxSign(tx.kind))}${esc(formatMoney(Number(tx.amount), currency))}</strong>
              </div>
            `).join('')}</div>
          </details>
        `}
        ${canEditExpected && !this.state.depositExpectedFormOpen ? `<button class="btn btn-ghost" data-action="deposit-expected-open">${icon('pencil', { size: 'sm' })}<span>${esc(t('tenancy.billing.deposit.set_expected'))}</span></button>` : ''}
        ${this.state.depositExpectedFormOpen ? this._depositExpectedFormHtml(c) : ''}
        ${canRecord && !this.state.depositTxFormOpen ? `<button class="btn btn-primary" data-action="deposit-tx-open">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.billing.deposit.record'))}</span></button>` : ''}
        ${this.state.depositTxFormOpen ? this._depositTxFormHtml(c, d) : ''}
      </div>
    `;
  }

  _depositExpectedFormHtml(c) {
    const f = this.state.depositExpectedForm;
    return `
      <form class="stack" data-deposit-expected-form>
        <label class="stack-2"><span>${esc(t('tenancy.billing.deposit.expected_amount', { currency: c.currency }))}</span>
          <input class="input" name="expected_amount" type="number" min="0" step="1" value="${esc(f.expected_amount)}" required />
        </label>
        <div class="row" style="justify-content:flex-end;gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="deposit-expected-cancel">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary">${esc(t('common.save'))}</button>
        </div>
      </form>
    `;
  }

  _depositTxFormHtml(c, d) {
    const f = this.state.depositTxForm;
    const kinds = ['RECEIVED', 'REFUNDED', 'FORFEITED', 'APPLIED_TO_RENT', 'ADJUSTMENT'];
    const held = d.held_balance || 0;
    return `
      <form class="stack" data-deposit-tx-form>
        <label class="stack-2"><span>${esc(t('tenancy.billing.deposit.kind'))}</span>
          <select class="input" name="kind">${kinds.map((k) => `<option value="${k}" ${f.kind === k ? 'selected' : ''}>${esc(t('tenancy.billing.deposit.kinds.' + k))}</option>`).join('')}</select>
        </label>
        <label class="stack-2"><span>${esc(t('tenancy.billing.deposit.amount', { currency: c.currency }))}</span>
          <input class="input" name="amount" type="number" min="1" step="1" value="${esc(f.amount)}" required />
          ${held > 0 && f.kind !== 'RECEIVED' && f.kind !== 'ADJUSTMENT' ? `<span class="faint">${esc(t('tenancy.billing.deposit.max_payout', { amount: formatMoney(held, c.currency) }))}</span>` : ''}
        </label>
        <label class="stack-2"><span>${esc(t('tenancy.billing.deposit.note'))} <span class="faint">(${esc(t('common.optional'))})</span></span>
          <input class="input" name="note" value="${esc(f.note)}" maxlength="255" />
        </label>
        <div class="row" style="justify-content:flex-end;gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="deposit-tx-cancel">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary">${esc(t('tenancy.billing.deposit.submit'))}</button>
        </div>
      </form>
    `;
  }

  _depositBadgeClass(status) {
    if (status === 'HELD') return 'badge-success';
    if (status === 'PARTIAL' || status === 'EXPECTED') return 'badge-warn';
    if (status === 'REFUNDED' || status === 'APPLIED') return 'badge-muted';
    if (status === 'FORFEITED') return 'badge-danger';
    return 'badge-muted';
  }

  _depositTxSign(kind) {
    return (kind === 'RECEIVED' || kind === 'ADJUSTMENT') ? '+' : '−';
  }

  _pendingFormHtml(c) {
    const f = this.state.pendingForm;
    const types = ['fee', 'utility', 'parking', 'storage', 'custom'];
    return `
      <form class="stack" data-pending-form>
        <label class="stack-2"><span>${esc(t('tenancy.billing.pending.type'))}</span>
          <select class="input" name="type">${types.map((tp) => `<option value="${tp}" ${f.type === tp ? 'selected' : ''}>${esc(t('tenancy.billing.pending.types.' + tp))}</option>`).join('')}</select>
        </label>
        <label class="stack-2"><span>${esc(t('tenancy.billing.pending.description'))}</span>
          <input class="input" name="description" value="${esc(f.description)}" required maxlength="255" />
        </label>
        <label class="stack-2"><span>${esc(t('tenancy.billing.pending.amount', { currency: c.currency }))}</span>
          <input class="input" name="amount" type="number" step="1" value="${esc(f.amount)}" required />
          <span class="faint">${esc(t('tenancy.billing.pending.amount_hint'))}</span>
        </label>
        <div class="row" style="justify-content:flex-end;gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="pending-cancel-form">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary">${esc(t('tenancy.billing.pending.submit'))}</button>
        </div>
      </form>
    `;
  }

  _docHtml(d, c) {
    const open = (d.total_amount || 0) - (d.paid_amount || 0);
    const docIcon = d.type === 'INVOICE' ? 'file-text' : 'receipt';
    const href = `#/documents/${encodeURIComponent(d.id)}`;
    return `
      <a class="list-item" href="${href}">
        ${icon(docIcon, { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(d.type)} · ${esc(d.number)}</strong>
          <span class="faint">${esc(formatDate(d.issue_date))} · ${esc(d.status)}</span>
        </div>
        <div class="stack-2" style="text-align:right">
          <strong>${esc(formatMoney(d.total_amount, c.currency))}</strong>
          ${d.type === 'PROFORMA' && open > 0 ? `<span class="faint">${esc(t('tenancy.billing.open', { amount: formatMoney(open, c.currency) }))}</span>` : ''}
        </div>
        ${icon('chevron-right', { class: 'icon-faint' })}
      </a>
    `;
  }

  _itemHtml(it, c) {
    const amt = esc(formatMoney(it.amount, c.currency));
    const isMeter = !!it.meter_reading_id;
    const qty = it.quantity != null ? String(it.quantity) : null;
    const uom = it.meter_unit_of_measure || '';
    const unitPrice = it.unit_price != null
      ? esc(formatMoney(it.unit_price, c.currency)) + (uom ? ` / ${esc(uom)}` : '')
      : null;
    const baseFee = it.base_fee != null && Number(it.base_fee) > 0
      ? esc(formatMoney(Number(it.base_fee), c.currency))
      : null;
    const consumptionLine = isMeter && qty !== null
      ? `${esc(qty)}${uom ? ' ' + esc(uom) : ''}${unitPrice ? ' \u00d7 ' + unitPrice : ''}`
      : null;
    const breakdown = [
      baseFee ? `base ${baseFee}` : null,
      consumptionLine,
    ].filter(Boolean).join(' + ');
    return `
      <div class="row" style="justify-content:space-between;gap:var(--space-3)">
        <div class="stack-2 grow">
          <span>${esc(it.description)}</span>
          ${breakdown ? `<span class="faint">${breakdown}</span>` : ''}
        </div>
        <strong>${amt}</strong>
      </div>
    `;
  }

  _badgeClass(s) {
    return s === 'ACTIVE' ? 'badge-success' : s === 'DRAFT' ? 'badge-warning' : 'badge-muted';
  }

  // Landlord-only: portal-access ("bekötés") panel. Three states keyed off the
  // linked tenant_parties row surfaced on the tenancy: connected (linked
  // account), invitation pending, or not connected (offer to invite). After
  // minting an invite we keep the share link in state so it survives the reload.
  _portalHtml(c) {
    const linked = !!c.tenant_user_id;
    const pending = !linked && !!c.invitation_id;
    const inv = this.state.portalInvite;
    const linkBlock = inv && inv.token ? `
      <div class="stack-2" style="margin-top:var(--space-2)">
        <span class="faint">${esc(t('tenancy.portal.share'))}</span>
        <div class="row" style="gap:var(--space-2)">
          <input class="input" readonly value="${esc(this._inviteLink(inv.token))}" data-portal-link style="flex:1" />
          <button class="btn btn-ghost btn-sm" data-action="copy-portal-link">${esc(t('tenancy.portal.copy'))}</button>
        </div>
      </div>` : '';
    if (linked) {
      return `<div class="row" style="gap:var(--space-2);align-items:center"><span class="faint">${esc(t('tenancy.portal.title'))}</span><span class="badge badge-success">${icon('check', { size: 'sm' })} ${esc(t('tenancy.portal.linked'))}</span></div>`;
    }
    if (pending) {
      const exp = c.invitation_expires_at ? ` · ${esc(t('tenancy.portal.expires', { date: c.invitation_expires_at }))}` : '';
      return `
        <div class="stack-2">
          <div class="row" style="gap:var(--space-2);align-items:center;flex-wrap:wrap">
            <span class="badge badge-warning">${esc(t('tenancy.portal.pending'))}</span><span class="faint">${exp}</span>
            <button class="btn btn-ghost btn-sm" data-action="invite-portal">${icon('send', { size: 'sm' })}<span>${esc(t('tenancy.portal.reinvite'))}</span></button>
          </div>
          ${linkBlock}
        </div>`;
    }
    return `
      <div class="stack-2">
        <span class="faint">${esc(t('tenancy.portal.explain_none'))}</span>
        <button class="btn btn-ghost" data-action="invite-portal" style="align-self:flex-start">${icon('send', { size: 'sm' })}<span>${esc(t('tenancy.portal.invite'))}</span></button>
        ${linkBlock}
      </div>`;
  }

  _inviteLink(token) {
    return `${window.location.origin}${window.location.pathname}#/invitations/by-token/${token}`;
  }

  _utilityFormHtml(c) {
    const list = this.state.meters;
    if (list === null) return `<div class="card stack"><div class="muted">${esc(t('tenancy.utility_form.loading_meters'))}</div></div>`;
    if (list.length === 0) {
      return `<div class="card stack">
        <div class="empty">${esc(t('tenancy.utility_form.empty'))}</div>
        <div class="row" style="justify-content:flex-end"><button class="btn btn-ghost" data-action="utility-cancel">${esc(t('common.cancel'))}</button></div>
      </div>`;
    }
    const selected = list.find((m) => m.id === this.state.utilityMeterId);
    const isFee = selected?.kind === 'fee';
    const latest = selected && !isFee ? this._latestAcceptedReading(selected) : null;
    const delta = latest && latest.delta !== null && latest.delta !== undefined
      ? parseFloat(latest.delta) : null;
    const rate = parseFloat(this.state.utilityRate);
    const unitPriceMinor = isFinite(rate) ? Math.round(rate * 100) : 0;
    const baseFee = parseFloat(this.state.utilityBaseFee);
    const baseFeeMinor = isFinite(baseFee) ? Math.round(baseFee * 100) : 0;
    const consumption = (delta !== null && unitPriceMinor > 0) ? Math.round(delta * unitPriceMinor) : 0;
    const totalPreview = baseFeeMinor + consumption;
    const canSubmit = selected && totalPreview > 0
      && (isFee || (latest && delta !== null) || baseFeeMinor > 0);
    return `
      <form class="card stack" data-utility-form>
        <h3>${esc(t('tenancy.utility_form.title'))}</h3>
        <p class="muted" style="margin:0">${esc(t('tenancy.utility_form.explain'))}</p>
        <div class="field">
          <label>${esc(t('tenancy.utility_form.meter'))}</label>
          <select class="select" name="meter_id">
            <option value="" ${!this.state.utilityMeterId ? 'selected' : ''}>${esc(t('tenancy.utility_form.pick_meter'))}</option>
            ${list.map((m) => `<option value="${esc(m.id)}" ${this.state.utilityMeterId === m.id ? 'selected' : ''}>${esc(m.unit_label)} · ${esc(m.kind)}${m.label ? ' · ' + esc(m.label) : ''}${m.kind === 'fee' ? '' : ` (${esc(m.unit_of_measure)})`}</option>`).join('')}
          </select>
        </div>
        ${selected && !isFee ? this._utilityReadingHtml(selected, latest, delta) : ''}
        ${selected && !isFee && selected.tier_quota_annual != null && selected.tier_price_above != null ? `
          <div class="muted">${esc(t('tenancy.utility_form.tier_hint', {
            quota: String(Number(selected.tier_quota_annual)),
            uom: selected.unit_of_measure,
            price: formatMoney(Number(selected.tier_price_above), c.currency),
          }))}</div>` : ''}
        ${selected ? `
          <div class="row" style="gap:var(--space-3)">
            <div class="field grow">
              <label>${esc(t('tenancy.utility_form.monthly_fee_currency', { currency: c.currency }))}</label>
              <input class="input" name="base_fee" type="number" step="0.01" min="0" value="${esc(this.state.utilityBaseFee)}" />
            </div>
            ${isFee ? '' : `
            <div class="field grow">
              <label>${esc(t('tenancy.utility_form.unit_price_currency', { currency: c.currency, uom: selected.unit_of_measure }))}</label>
              <input class="input" name="rate" type="number" step="0.01" min="0" value="${esc(this.state.utilityRate)}" />
            </div>`}
          </div>
          <div class="field">
            <label class="faint">${esc(t('tenancy.utility_form.total'))}</label>
            <div data-role="utility-total">${totalPreview > 0 ? esc(formatMoney(totalPreview, c.currency)) : '<span class="faint">—</span>'}</div>
          </div>
          <div class="field">
            <label>${esc(t('tenancy.utility_form.description'))}</label>
            <input class="input" name="description" value="${esc(this.state.utilityDescription)}" required />
          </div>
        ` : ''}
        <div class="row" style="justify-content:space-between">
          <span></span>
          <div class="row" style="gap:var(--space-3)">
            <button type="button" class="btn btn-ghost" data-action="utility-cancel">${esc(t('common.cancel'))}</button>
            <button type="submit" class="btn btn-primary" data-role="utility-submit" ${canSubmit ? '' : 'disabled'}>${esc(t('tenancy.utility_form.submit'))}</button>
          </div>
        </div>
      </form>
    `;
  }

  _latestAcceptedReading(meter) {
    return (meter.readings || []).find((r) => r.status === 'ACCEPTED' || r.status === undefined) || null;
  }

  _refreshUtilityTotal() {
    const c = this.state.tenancy;
    const list = this.state.meters;
    if (!c || !list) return;
    const selected = list.find((m) => m.id === this.state.utilityMeterId);
    if (!selected) return;
    const isFee = selected.kind === 'fee';
    const latest = !isFee ? this._latestAcceptedReading(selected) : null;
    const delta = latest && latest.delta !== null && latest.delta !== undefined
      ? parseFloat(latest.delta) : null;
    const rate = parseFloat(this.state.utilityRate);
    const unitPriceMinor = isFinite(rate) ? Math.round(rate * 100) : 0;
    const baseFee = parseFloat(this.state.utilityBaseFee);
    const baseFeeMinor = isFinite(baseFee) ? Math.round(baseFee * 100) : 0;
    const consumption = (delta !== null && unitPriceMinor > 0) ? Math.round(delta * unitPriceMinor) : 0;
    const totalPreview = baseFeeMinor + consumption;
    const totalEl = this.el.querySelector('[data-role="utility-total"]');
    if (totalEl) {
      totalEl.innerHTML = totalPreview > 0
        ? esc(formatMoney(totalPreview, c.currency))
        : '<span class="faint">—</span>';
    }
    const submitEl = this.el.querySelector('[data-role="utility-submit"]');
    if (submitEl) {
      const canSubmit = totalPreview > 0 && (isFee || (latest && delta !== null) || baseFeeMinor > 0);
      submitEl.disabled = !canSubmit;
    }
  }

  _utilityReadingHtml(meter, latest, delta) {
    if (!latest) return `<div class="warn-banner">${esc(t('tenancy.utility_form.no_accepted'))}</div>`;
    if (delta === null) return `<div class="warn-banner">${esc(t('tenancy.utility_form.only_one'))}</div>`;
    return `
      <div class="muted stack-2">
        <div>${esc(t('tenancy.utility_form.latest_reading'))}<strong>${esc(latest.reading_value)} ${esc(meter.unit_of_measure)}</strong> on ${esc(latest.read_at)}</div>
        <div>${esc(t('tenancy.utility_form.previous_reading'))}<strong>${esc(latest.previous_value)} ${esc(meter.unit_of_measure)}</strong></div>
        <div>${esc(t('tenancy.utility_form.consumption'))}<strong>${esc(latest.delta)} ${esc(meter.unit_of_measure)}</strong></div>
      </div>
    `;
  }

  mount() {
    this._hydrateProtectedAssets();
    this.el.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
      this.setState({ tab: b.getAttribute('data-tab') });
      if (this.state.tab === 'billing' && this.state.documents === null) this._loadBilling();
      if (this.state.tab === 'meters' && this.state.meters === null) this._loadMeters();
      if (this.state.tab === 'handovers' && this.state.handovers === null) this._loadHandovers();
      if (this.state.tab === 'agreements' && this.state.agreements === null) this._loadAgreements();
      if (this.state.tab === 'maintenance' && this.state.maintenance === null) this._loadMaintenance();
    }));
    const uploadForm = this.el.querySelector('[data-agreement-upload]');
    if (uploadForm) uploadForm.addEventListener('submit', (e) => { e.preventDefault(); this._uploadAgreement(uploadForm); });
    this._wireMaintenance();
    if (this.state.tab === 'messages') this._wireChat(); else this._stopChatPoll();
    if (this.state.tab === 'members' && this.state.tenancy) {
      const isLandlord = !!(auth.user && this.state.tenancy.landlord_user_id === auth.user.id);
      attachMembersPanel(this.el, {
        entity: 'tenancy', entityId: this.id, canManage: isLandlord,
      });
    }
    this.el.querySelectorAll('[data-tenant-apply-field]').forEach((cb) => cb.addEventListener('change', () => {
      const key = cb.getAttribute('data-tenant-apply-field');
      const next = { ...(this.state.tenantApplyChecked || {}) };
      next[key] = cb.checked;
      this.setState({ tenantApplyChecked: next });
    }));
    const tdApply = this.el.querySelector('[data-action="tenant-data-apply"]');
    if (tdApply) tdApply.addEventListener('click', () => this._applyTenantData());
    const a = this.el.querySelector('[data-action="activate"]');
    if (a) a.addEventListener('click', () => this._activate());
    const e = this.el.querySelector('[data-action="end"]');
    if (e) e.addEventListener('click', () => this._end());
    const ip = this.el.querySelector('[data-action="invite-portal"]');
    if (ip) ip.addEventListener('click', () => this._invitePortal());
    const cp = this.el.querySelector('[data-action="copy-portal-link"]');
    if (cp) cp.addEventListener('click', () => this._copyPortalLink());
    this.el.querySelectorAll('[data-action="handover-create"]').forEach((b) => b.addEventListener('click', () => {
      this._createHandover(b.getAttribute('data-type'));
    }));
    this.el.querySelectorAll('[data-action="handover-pdf"]').forEach((b) => b.addEventListener('click', () => {
      this._downloadHandoverPdf(b);
    }));
    this.el.querySelectorAll('[data-action="agreement-pdf"]').forEach((b) => b.addEventListener('click', () => {
      this._downloadAgreementPdf(b);
    }));
    const amOpen = this.el.querySelector('[data-action="amend-open"]');
    if (amOpen) amOpen.addEventListener('click', () => this._openAmendForm());
    const amCancel = this.el.querySelector('[data-action="amend-cancel"]');
    if (amCancel) amCancel.addEventListener('click', () => this.setState({ amendOpen: false, error: null }));
    const amForm = this.el.querySelector('[data-amend-form]');
    if (amForm) {
      const dateInput = amForm.querySelector('input[name="start_date"]');
      if (dateInput) dateInput.addEventListener('input', () => { this.state.amendStartDate = dateInput.value; });
      const endInput = amForm.querySelector('input[name="end_date"]');
      if (endInput) endInput.addEventListener('input', () => { this.state.amendEndDate = endInput.value; });
      amForm.querySelectorAll('[data-amend-unit]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const id = cb.getAttribute('data-amend-unit');
          const next = { ...this.state.amendDroppedUnits };
          if (cb.checked) delete next[id]; else next[id] = true;
          this.setState({ amendDroppedUnits: next });
        });
      });
      amForm.querySelectorAll('[data-amend-add-unit]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const id = cb.getAttribute('data-amend-add-unit');
          const next = { ...this.state.amendAddedRents };
          if (cb.checked) next[id] = ''; else delete next[id];
          this.setState({ amendAddedRents: next });
        });
      });
      amForm.querySelectorAll('[data-amend-add-rent]').forEach((input) => {
        input.addEventListener('input', () => {
          const id = input.getAttribute('data-amend-add-rent');
          this.state.amendAddedRents = { ...this.state.amendAddedRents, [id]: input.value };
        });
      });
      amForm.querySelectorAll('[data-amend-kept-rent]').forEach((input) => {
        input.addEventListener('input', () => {
          const id = input.getAttribute('data-amend-kept-rent');
          this.state.amendKeptRents = { ...this.state.amendKeptRents, [id]: input.value };
        });
      });
      amForm.addEventListener('submit', (ev) => { ev.preventDefault(); this._submitAmend(); });
    }
    const edOpen = this.el.querySelector('[data-action="edit-open"]');
    if (edOpen) edOpen.addEventListener('click', () => this._openEditForm());
    const edCancel = this.el.querySelector('[data-action="edit-cancel"]');
    if (edCancel) edCancel.addEventListener('click', () => this.setState({ editOpen: false, error: null }));
    const delDraft = this.el.querySelector('[data-action="delete-draft"]');
    if (delDraft) delDraft.addEventListener('click', () => this._deleteDraft());
    const edForm = this.el.querySelector('[data-edit-form]');
    if (edForm) {
      ['start_date', 'end_date', 'currency', 'notes', 'deposit_amount'].forEach((name) => {
        const input = edForm.querySelector(`[name="${name}"]`);
        if (!input) return;
        input.addEventListener('input', () => {
          if (name === 'currency') {
            this.setState({ editForm: { ...this.state.editForm, currency: input.value } });
          } else {
            this.state.editForm = { ...this.state.editForm, [name]: input.value };
          }
        });
      });
      edForm.querySelectorAll('[data-edit-unit]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const id = cb.getAttribute('data-edit-unit');
          const next = { ...this.state.editDroppedUnits };
          if (cb.checked) delete next[id]; else next[id] = true;
          this.setState({ editDroppedUnits: next });
        });
      });
      edForm.querySelectorAll('[data-edit-add-unit]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const id = cb.getAttribute('data-edit-add-unit');
          const next = { ...this.state.editAddedRents };
          if (cb.checked) next[id] = ''; else delete next[id];
          this.setState({ editAddedRents: next });
        });
      });
      edForm.querySelectorAll('[data-edit-add-rent]').forEach((input) => {
        input.addEventListener('input', () => {
          const id = input.getAttribute('data-edit-add-rent');
          this.state.editAddedRents = { ...this.state.editAddedRents, [id]: input.value };
        });
      });
      edForm.querySelectorAll('[data-edit-kept-rent]').forEach((input) => {
        input.addEventListener('input', () => {
          const id = input.getAttribute('data-edit-kept-rent');
          this.state.editKeptRents = { ...this.state.editKeptRents, [id]: input.value };
        });
      });
      edForm.addEventListener('submit', (ev) => { ev.preventDefault(); this._submitEdit(); });
    }
    const p = this.el.querySelector('[data-action="proforma"]');
    if (p) p.addEventListener('click', () => this._createProforma());
    const pay = this.el.querySelector('[data-action="payment"]');
    if (pay) pay.addEventListener('click', () => this._recordPayment());

    const pendOpen = this.el.querySelector('[data-action="pending-open"]');
    if (pendOpen) pendOpen.addEventListener('click', () => this.setState({ pendingFormOpen: true }));
    const pendCancelForm = this.el.querySelector('[data-action="pending-cancel-form"]');
    if (pendCancelForm) pendCancelForm.addEventListener('click', () => this.setState({
      pendingFormOpen: false,
      pendingForm: { type: 'fee', description: '', amount: '' },
    }));
    this.el.querySelectorAll('[data-action="pending-cancel"]').forEach((b) => {
      b.addEventListener('click', () => this._cancelPendingItem(b.getAttribute('data-id')));
    });
    const pendForm = this.el.querySelector('[data-pending-form]');
    if (pendForm) {
      pendForm.querySelectorAll('input, select').forEach((input) => {
        input.addEventListener('input', () => {
          this.state.pendingForm = {
            ...this.state.pendingForm,
            [input.name]: input.value,
          };
        });
      });
      pendForm.addEventListener('submit', (ev) => { ev.preventDefault(); this._addPendingItem(); });
    }

    const depExpOpen = this.el.querySelector('[data-action="deposit-expected-open"]');
    if (depExpOpen) depExpOpen.addEventListener('click', () => this.setState({
      depositExpectedFormOpen: true,
      depositExpectedForm: { expected_amount: String(this.state.deposit?.expected_amount ?? '') },
    }));
    const depExpCancel = this.el.querySelector('[data-action="deposit-expected-cancel"]');
    if (depExpCancel) depExpCancel.addEventListener('click', () => this.setState({
      depositExpectedFormOpen: false,
      depositExpectedForm: { expected_amount: '' },
    }));
    const depExpForm = this.el.querySelector('[data-deposit-expected-form]');
    if (depExpForm) {
      depExpForm.querySelectorAll('input').forEach((input) => {
        input.addEventListener('input', () => {
          this.state.depositExpectedForm = {
            ...this.state.depositExpectedForm,
            [input.name]: input.value,
          };
        });
      });
      depExpForm.addEventListener('submit', (ev) => { ev.preventDefault(); this._saveDepositExpected(); });
    }
    const depTxOpen = this.el.querySelector('[data-action="deposit-tx-open"]');
    if (depTxOpen) depTxOpen.addEventListener('click', () => this.setState({ depositTxFormOpen: true }));
    const depTxCancel = this.el.querySelector('[data-action="deposit-tx-cancel"]');
    if (depTxCancel) depTxCancel.addEventListener('click', () => this.setState({
      depositTxFormOpen: false,
      depositTxForm: { kind: 'RECEIVED', amount: '', note: '' },
    }));
    const depTxForm = this.el.querySelector('[data-deposit-tx-form]');
    if (depTxForm) {
      depTxForm.querySelectorAll('input, select').forEach((input) => {
        const evt = input.tagName === 'SELECT' ? 'change' : 'input';
        input.addEventListener(evt, () => {
          const next = { ...this.state.depositTxForm, [input.name]: input.value };
          if (input.name === 'kind') this.setState({ depositTxForm: next });
          else this.state.depositTxForm = next;
        });
      });
      depTxForm.addEventListener('submit', (ev) => { ev.preventDefault(); this._recordDepositTx(); });
    }

    const uOpen = this.el.querySelector('[data-action="utility-open"]');
    if (uOpen) uOpen.addEventListener('click', () => this._openUtilityForm());
    const uCancel = this.el.querySelector('[data-action="utility-cancel"]');
    if (uCancel) uCancel.addEventListener('click', () => this._closeUtilityForm());
    const form = this.el.querySelector('[data-utility-form]');
    if (form) {
      const sel = form.querySelector('select[name="meter_id"]');
      if (sel) sel.addEventListener('change', () => this._selectUtilityMeter(sel.value));
      const rate = form.querySelector('input[name="rate"]');
      if (rate) rate.addEventListener('input', () => {
        this.state.utilityRate = rate.value;
        this._refreshUtilityTotal();
      });
      const baseFee = form.querySelector('input[name="base_fee"]');
      if (baseFee) baseFee.addEventListener('input', () => {
        this.state.utilityBaseFee = baseFee.value;
        this._refreshUtilityTotal();
      });
      const desc = form.querySelector('input[name="description"]');
      if (desc) desc.addEventListener('input', () => { this.state.utilityDescription = desc.value; });
      form.addEventListener('submit', (ev) => { ev.preventDefault(); this._saveUtilityProforma(); });
    }

    this.el.querySelectorAll('[data-action="reading-open"]').forEach((b) => b.addEventListener('click', () => {
      this._openReadingForm(b.getAttribute('data-meter-id'));
    }));
    this.el.querySelectorAll('[data-action="reading-cancel"]').forEach((b) => b.addEventListener('click', () => {
      this._closeReadingForm(b.getAttribute('data-meter-id'));
    }));
    this.el.querySelectorAll('[data-reading-form]').forEach((f) => {
      const meterId = f.getAttribute('data-reading-form');
      const valueInput = f.querySelector('input[name="reading_value"]');
      const dateInput = f.querySelector('input[name="read_at"]');
      const photoInput = f.querySelector('input[name="photo"]');
      if (valueInput) valueInput.addEventListener('input', () => { this._updateReadingForm(meterId, { value: valueInput.value }); });
      if (dateInput) dateInput.addEventListener('input', () => {
        this._updateReadingForm(meterId, { read_at: dateInput.value });
        this._refreshSameDayWarning(f, meterId, dateInput.value);
      });
      f.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const file = photoInput && photoInput.files && photoInput.files[0] ? photoInput.files[0] : null;
        this._saveReading(meterId, file);
      });
    });

    this.el.querySelectorAll('[data-action="reading-accept"]').forEach((b) => b.addEventListener('click', () => {
      this._reviewReading(b.getAttribute('data-reading-id'), 'accept');
    }));
    this.el.querySelectorAll('[data-action="reading-reject"]').forEach((b) => b.addEventListener('click', () => {
      this._reviewReading(b.getAttribute('data-reading-id'), 'reject');
    }));
    this.el.querySelectorAll('[data-action="reading-resubmit"]').forEach((b) => b.addEventListener('click', () => {
      this._resubmitReading(b.getAttribute('data-reading-id'));
    }));
    this.el.querySelectorAll('[data-action="reading-delete"]').forEach((b) => b.addEventListener('click', () => {
      this._deleteReading(b.getAttribute('data-reading-id'));
    }));

    if (this.state.tenancy === null) this._loadTenancy();
  }

  // Seed the amend/renewal form (also used by the ?action=amend deep link).
  _openAmendForm() {
    const c = this.state.tenancy;
    if (!c) return;
    const keptRents = {};
    (c.units || []).forEach((u) => {
      keptRents[u.unit_id] = String(Math.round((u.rent_amount || 0) / 100));
    });
    this.setState({
      amendOpen: true, amendStartDate: '', amendEndDate: '', amendDroppedUnits: {},
      amendAvailableUnits: null, amendAddedRents: {},
      amendKeptRents: keptRents, error: null,
    });
    this._loadAmendAvailableUnits();
  }

  async _loadTenancy() {
    try {
      const tenancy = await api.get(`/tenancies/${this.id}`);
      this.setState({ tenancy });
      // Deep-linked tabs need their lazy fetch kicked off here — the
      // click handlers in mount() only fire on user interaction.
      if (this.state.tab === 'billing') this._loadBilling();
      if (this.state.tab === 'meters' && this.state.meters === null) this._loadMeters();
      if (this.state.tab === 'handovers' && this.state.handovers === null) this._loadHandovers();
      if (this.state.tab === 'agreements' && this.state.agreements === null) this._loadAgreements();
      if (this.state.tab === 'maintenance' && this.state.maintenance === null) this._loadMaintenance();
      if (this._autoAction === 'amend') {
        this._autoAction = null;
        const isLandlord = !!(auth.user && tenancy.landlord_user_id === auth.user.id);
        if (isLandlord && tenancy.status === 'ACTIVE') this._openAmendForm();
      }
      if (auth.user && tenancy.landlord_user_id === auth.user.id) this._loadTenantData();
      // Unread chat badge on the messages tab; failures stay silent.
      api.get('/messages/unread').then((res) => {
        const n = res && res.by_tenancy ? Number(res.by_tenancy[this.id] || 0) : 0;
        if (n > 0) this.setState({ msgUnread: n });
      }).catch(() => {});
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.load') });
    }
  }

  async _loadTenantData() {
    try {
      const td = await api.get(`/tenancies/${this.id}/tenant-data`);
      this.setState({ tenantData: td });
    } catch (_) {
      // Non-fatal: the panel just stays hidden when the endpoint is
      // unavailable (older backend, transient error, no manage rights).
    }
  }

  async _applyTenantData() {
    const td = this.state.tenantData;
    if (!td || !td.proposed) return;
    const checked = this.state.tenantApplyChecked || {};
    const current = td.current || {};
    const fields = {};
    for (const key of Object.keys(td.proposed)) {
      const isChecked = checked[key] !== undefined
        ? checked[key]
        : (current[key] || '').trim() !== (td.proposed[key] || '').trim();
      if (isChecked) fields[key] = td.proposed[key];
    }
    if (Object.keys(fields).length === 0) return;
    this.setState({ tenantApplyBusy: true });
    try {
      const res = await api.post(`/tenancies/${this.id}/apply-tenant-data`, { fields });
      toast.success(t('tenancy.tenant_data.applied', { count: (res.updated || []).length }));
      this.setState({ tenantApplyBusy: false, tenantApplyChecked: {} });
      this._loadTenantData();
    } catch (err) {
      this.setState({ tenantApplyBusy: false });
      toast.error(err instanceof ApiError ? err.message : t('tenancy.tenant_data.errors.apply'));
    }
  }

  async _loadBilling() {
    try {
      const [docs, pays, pending, deposit] = await Promise.all([
        api.get(`/tenancies/${this.id}/documents`),
        api.get(`/tenancies/${this.id}/payments`),
        api.get(`/tenancies/${this.id}/pending-items`),
        api.get(`/tenancies/${this.id}/deposit`),
      ]);
      this.setState({
        documents: docs.data || [],
        payments: pays.data || [],
        pendingItems: pending.data || [],
        deposit,
      });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.load_billing') });
    }
  }

  async _loadHandovers() {
    try {
      const res = await api.get(`/tenancies/${this.id}/handovers`);
      this.setState({ handovers: res.data || [] });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.load_handovers') });
    }
  }

  async _loadAgreements() {
    try {
      const res = await api.get(`/tenancies/${this.id}/agreements`);
      this.setState({ agreements: res.data || [] });
    } catch (_) {
      // Non-fatal: agreements panel is optional. Surface as empty list
      // so the user can still attach a new PDF without seeing a banner.
      this.setState({ agreements: [] });
    }
  }

  /** TRACK_ONLY upload: POST a multipart/form-data body to the same
   *  /tenancies/{id}/agreements endpoint with kind=ATTACHED. The server
   *  validates mime + size and stores the bytes inline. */
  async _uploadAgreement(form) {
    const fileInput = form.querySelector('[data-agreement-file]');
    const titleInput = form.querySelector('[data-agreement-title]');
    const errSlot = this.el.querySelector('[data-agreement-upload-error]');
    if (errSlot) errSlot.innerHTML = '';
    if (!fileInput.files || fileInput.files.length === 0) {
      if (errSlot) errSlot.innerHTML = `<div class="error-banner">${esc(t('tenancy.agreements.upload_no_file'))}</div>`;
      return;
    }
    const file = fileInput.files[0];
    if (file.type !== 'application/pdf') {
      if (errSlot) errSlot.innerHTML = `<div class="error-banner">${esc(t('tenancy.agreements.upload_only_pdf'))}</div>`;
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = String(reader.result || '').split(',')[1] || '';
        await api.post(`/tenancies/${this.id}/agreements`, {
          kind: 'ATTACHED',
          source: 'UPLOAD',
          title: titleInput.value || file.name,
          file_name: file.name,
          mime_type: 'application/pdf',
          pdf_base64: base64,
        });
        this.setState({ agreements: null });
        this._loadAgreements();
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : t('tenancy.agreements.upload_error');
        if (errSlot) errSlot.innerHTML = `<div class="error-banner">${esc(msg)}</div>`;
      }
    };
    reader.readAsDataURL(file);
  }

  async _createHandover(type) {
    try {
      const h = await api.post(`/tenancies/${this.id}/handovers`, { type });
      window.location.hash = `#/handovers/${h.id}`;
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _downloadHandoverPdf(btn) {
    if (btn.disabled) return;
    const id = btn.getAttribute('data-id');
    btn.disabled = true;
    try {
      const blob = await api.get(
        `/handovers/${encodeURIComponent(id)}/pdf?lang=${encodeURIComponent(getLocale())}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `handover-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('handover.download_error'));
    } finally {
      btn.disabled = false;
    }
  }

  async _downloadAgreementPdf(btn) {
    if (btn.disabled) return;
    const id = btn.getAttribute('data-id');
    const name = btn.getAttribute('data-name') || `agreement-${id}.pdf`;
    btn.disabled = true;
    try {
      const blob = await api.get(
        `/tenancy-agreements/${encodeURIComponent(id)}/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('tenancy.agreements.download_error'));
    } finally {
      btn.disabled = false;
    }
  }

  async _saveDepositExpected() {
    const raw = this.state.depositExpectedForm.expected_amount;
    const amt = Math.trunc(Number(raw));
    if (!Number.isFinite(amt) || amt < 0) { this.setState({ error: t('tenancy.errors.amount_invalid') }); return; }
    try {
      const updated = await api.patch(`/tenancies/${this.id}/deposit`, { expected_amount: amt });
      this.setState({
        deposit: updated,
        depositExpectedFormOpen: false,
        depositExpectedForm: { expected_amount: '' },
      });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _recordDepositTx() {
    const f = this.state.depositTxForm;
    const amt = Math.trunc(Number(f.amount));
    if (!Number.isFinite(amt) || amt <= 0) { this.setState({ error: t('tenancy.errors.amount_invalid') }); return; }
    try {
      const updated = await api.post(`/tenancies/${this.id}/deposit/transactions`, {
        kind: f.kind,
        amount: amt,
        note: (f.note || '').trim() || undefined,
      });
      this.setState({
        deposit: updated,
        depositTxFormOpen: false,
        depositTxForm: { kind: 'RECEIVED', amount: '', note: '' },
        pendingItems: null,
      });
      this._loadBilling();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409
          && err.data?.error?.details?.reason === 'deposit_overdraw') {
        this.setState({ error: t('tenancy.billing.deposit.errors.overdraw', {
          amount: formatMoney(err.data.error.details.held_balance, this.state.tenancy?.currency || 'HUF'),
        }) });
        return;
      }
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _addPendingItem() {
    const f = this.state.pendingForm;
    const desc = (f.description || '').trim();
    const amt = Math.trunc(Number(f.amount));
    if (!desc) { this.setState({ error: t('tenancy.billing.pending.errors.description_required') }); return; }
    if (!Number.isFinite(amt) || amt === 0) { this.setState({ error: t('tenancy.billing.pending.errors.amount_required') }); return; }
    try {
      await api.post(`/tenancies/${this.id}/pending-items`, {
        type: f.type, description: desc, amount: amt,
      });
      this.setState({
        pendingFormOpen: false,
        pendingForm: { type: 'fee', description: '', amount: '' },
        pendingItems: null,
      });
      this._loadBilling();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _cancelPendingItem(id) {
    if (!confirm(t('tenancy.billing.pending.cancel_confirm'))) return;
    try {
      await api.delete(`/pending-items/${id}`);
      this.setState({ pendingItems: null });
      this._loadBilling();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _activate() {
    const c = this.state.tenancy;
    // Activation is allowed without the tenant accepting the invitation
    // (track-only tenancy) — but make the landlord confirm that choice.
    if (c && !c.tenant_user_id && !confirm(t('tenancy.activate_without_tenant_confirm'))) return;
    try {
      const tenancy = await api.post(`/tenancies/${this.id}/activate`);
      this.setState({ tenancy });
    } catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') }); }
  }

  async _invitePortal() {
    const c = this.state.tenancy;
    const partyId = c && c.tenant_party_id;
    if (!partyId) return;
    const btn = this.el.querySelector('[data-action="invite-portal"]');
    if (btn) btn.disabled = true;
    try {
      const res = await api.post(`/tenant-parties/${partyId}/invite`, {});
      this.setState({ portalInvite: res });
      toast.success(t('tenancy.portal.sent'));
      this._loadTenancy();
    } catch (err) {
      if (btn) btn.disabled = false;
      toast.error(err instanceof ApiError ? err.message : t('tenancy.portal.error'));
    }
  }

  _copyPortalLink() {
    const input = this.el.querySelector('[data-portal-link]');
    if (!input) return;
    const done = () => toast.success(t('tenancy.portal.copied'));
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).then(done).catch(() => { input.select(); document.execCommand('copy'); done(); });
    } else {
      input.select(); document.execCommand('copy'); done();
    }
  }

  async _end() {
    if (!confirm(t('tenancy.end_confirm'))) return;
    try {
      const tenancy = await api.post(`/tenancies/${this.id}/end`, { end_date: new Date().toISOString().slice(0, 10) });
      this.setState({ tenancy });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409
          && err.data?.error?.details?.reason === 'pending_items_outstanding') {
        const count = err.data.error.details.pending_count || 0;
        this.setState({
          error: t('tenancy.errors.end_blocked_pending', { count }),
          tab: 'billing',
          documents: null,
        });
        this._loadBilling();
        return;
      }
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _submitAmend() {
    const c = this.state.tenancy;
    if (!c) return;
    const today = new Date().toISOString().slice(0, 10);
    const minStart = c.start_date && c.start_date >= today ? c.start_date : today;
    const start = this.state.amendStartDate || minStart;
    const dropped = this.state.amendDroppedUnits || {};
    const added = this.state.amendAddedRents || {};
    const keptRents = this.state.amendKeptRents || {};
    const units = [];
    for (const u of (c.units || [])) {
      if (dropped[u.unit_id]) continue;
      const raw = Object.prototype.hasOwnProperty.call(keptRents, u.unit_id)
        ? keptRents[u.unit_id]
        : String(Math.round((u.rent_amount || 0) / 100));
      const main = parseFloat(raw);
      if (!Number.isFinite(main) || main < 0) {
        this.setState({ error: t('tenancy.amend_form.kept_rent_required') });
        return;
      }
      units.push({ unit_id: u.unit_id, rent_amount: Math.round(main * 100) });
    }
    const addedEntries = Object.entries(added);
    for (const [unitId, raw] of addedEntries) {
      const main = parseFloat(raw);
      if (!Number.isFinite(main) || main < 0) {
        this.setState({ error: t('tenancy.amend_form.add_unit_rent_required') });
        return;
      }
      units.push({ unit_id: unitId, rent_amount: Math.round(main * 100) });
    }
    if (units.length === 0) {
      this.setState({ error: t('tenancy.amend_form.keep_one') });
      return;
    }
    const confirmKey = units.length === 1 ? 'tenancy.amend_form.confirm_one' : 'tenancy.amend_form.confirm_other';
    if (!confirm(t(confirmKey, { date: start, count: units.length }))) return;
    this.setState({ amendBusy: true, error: null });
    try {
      // end_date only travels when set — omitted means open-ended, which
      // is also what the backend defaults to.
      const payload = { start_date: start, units };
      const end = (this.state.amendEndDate || '').trim();
      if (end !== '') payload.end_date = end;
      const next = await api.post(`/tenancies/${this.id}/amend`, payload);
      window.location.hash = `#/tenancies/${next.id}`;
    } catch (err) {
      this.setState({ amendBusy: false, error: err instanceof ApiError ? err.message : t('tenancy.amend_form.error') });
    }
  }

  async _loadAmendAvailableUnits() {
    const c = this.state.tenancy;
    if (!c) return;
    const propertyIds = Array.from(new Set((c.units || []).map((u) => u.property_id).filter(Boolean)));
    if (propertyIds.length === 0) {
      this.setState({ amendAvailableUnits: [] });
      return;
    }
    const onTenancy = new Set((c.units || []).map((u) => u.unit_id));
    try {
      const results = await Promise.all(propertyIds.map((pid) => api.get(`/properties/${pid}/units`)));
      const merged = [];
      results.forEach((res, idx) => {
        const propertyName = (c.units || []).find((u) => u.property_id === propertyIds[idx])?.property_name || '';
        (res.data || []).forEach((u) => {
          if (onTenancy.has(u.id)) return;
          merged.push({ ...u, property_name: propertyName });
        });
      });
      this.setState({ amendAvailableUnits: merged });
    } catch (_) {
      this.setState({ amendAvailableUnits: [] });
    }
  }

  _openEditForm() {
    const c = this.state.tenancy;
    if (!c) return;
    const keptRents = {};
    (c.units || []).forEach((u) => {
      keptRents[u.unit_id] = String(Math.round((u.rent_amount || 0) / 100));
    });
    this.setState({
      editOpen: true,
      editForm: {
        start_date: c.start_date || '',
        end_date: c.end_date || '',
        currency: c.currency || '',
        notes: c.notes || '',
        deposit_amount: c.deposit_amount != null ? String(Math.round(Number(c.deposit_amount) / 100)) : '',
      },
      editDroppedUnits: {},
      editKeptRents: keptRents,
      editAvailableUnits: null,
      editAddedRents: {},
      error: null,
    });
    this._loadEditAvailableUnits();
  }

  async _loadEditAvailableUnits() {
    const c = this.state.tenancy;
    if (!c) return;
    const propertyIds = Array.from(new Set((c.units || []).map((u) => u.property_id).filter(Boolean)));
    if (propertyIds.length === 0) {
      this.setState({ editAvailableUnits: [] });
      return;
    }
    const onTenancy = new Set((c.units || []).map((u) => u.unit_id));
    try {
      const results = await Promise.all(propertyIds.map((pid) => api.get(`/properties/${pid}/units`)));
      const merged = [];
      results.forEach((res, idx) => {
        const propertyName = (c.units || []).find((u) => u.property_id === propertyIds[idx])?.property_name || '';
        (res.data || []).forEach((u) => {
          if (onTenancy.has(u.id)) return;
          merged.push({ ...u, property_name: propertyName });
        });
      });
      this.setState({ editAvailableUnits: merged });
    } catch (_) {
      this.setState({ editAvailableUnits: [] });
    }
  }

  async _submitEdit() {
    const c = this.state.tenancy;
    if (!c) return;
    const f = this.state.editForm || {};
    const dropped = this.state.editDroppedUnits || {};
    const added = this.state.editAddedRents || {};
    const keptRents = this.state.editKeptRents || {};
    const units = [];
    for (const u of (c.units || [])) {
      if (dropped[u.unit_id]) continue;
      const raw = Object.prototype.hasOwnProperty.call(keptRents, u.unit_id)
        ? keptRents[u.unit_id]
        : String(Math.round((u.rent_amount || 0) / 100));
      const main = parseFloat(raw);
      if (!Number.isFinite(main) || main < 0) {
        this.setState({ error: t('tenancy.edit_form.rent_required') });
        return;
      }
      units.push({ unit_id: u.unit_id, rent_amount: Math.round(main * 100) });
    }
    for (const [unitId, raw] of Object.entries(added)) {
      const main = parseFloat(raw);
      if (!Number.isFinite(main) || main < 0) {
        this.setState({ error: t('tenancy.edit_form.rent_required') });
        return;
      }
      units.push({ unit_id: unitId, rent_amount: Math.round(main * 100) });
    }
    if (units.length === 0) {
      this.setState({ error: t('tenancy.edit_form.keep_one') });
      return;
    }
    const payload = {
      start_date: f.start_date,
      end_date: f.end_date || null,
      currency: (f.currency || '').toUpperCase(),
      notes: f.notes || null,
      units,
    };
    if (f.deposit_amount !== '' && f.deposit_amount != null) {
      const dep = parseFloat(f.deposit_amount);
      if (!Number.isFinite(dep) || dep < 0) {
        this.setState({ error: t('tenancy.edit_form.deposit_invalid') });
        return;
      }
      payload.deposit_amount = Math.round(dep * 100);
    } else {
      payload.deposit_amount = 0;
    }
    this.setState({ editBusy: true, error: null });
    try {
      const updated = await api.patch(`/tenancies/${this.id}`, payload);
      this.setState({ tenancy: updated, editOpen: false, editBusy: false });
    } catch (err) {
      this.setState({ editBusy: false, error: err instanceof ApiError ? err.message : t('tenancy.edit_form.error') });
    }
  }

  async _deleteDraft() {
    if (!confirm(t('tenancy.delete_draft_confirm'))) return;
    try {
      await api.delete(`/tenancies/${this.id}`);
      window.location.hash = '#/tenancies';
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _createProforma() {
    await this._postProforma({});
  }

  async _postProforma(payload) {
    try {
      await api.post(`/tenancies/${this.id}/proformas`, payload);
      this.setState({ documents: null });
      this._loadBilling();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409
          && err.data?.error?.details?.duplicate === 'proforma_in_month') {
        const d = err.data.error.details;
        this.setState({ error: t('tenancy.billing.duplicate_proforma', {
          number: d.existing_document_number,
          date: d.existing_issue_date,
        }) });
        return;
      }
      // Agency-managed property with no designated billing issuer — the
      // backend refuses to issue in the staff creator's name.
      if (err instanceof ApiError && err.status === 422
          && err.data?.error?.details?.issuer === 'not_designated') {
        this.setState({ error: t('tenancy.billing.issuer_not_designated') });
        return;
      }
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') });
    }
  }

  async _recordPayment() {
    const raw = prompt(t('tenancy.payment_prompt'));
    if (!raw) return;
    const amount = Math.round(parseFloat(raw) * 100);
    if (!amount || amount <= 0) { this.setState({ error: t('tenancy.errors.amount_invalid') }); return; }
    const method = prompt(t('tenancy.payment_method_prompt'), 'transfer') || 'transfer';
    try {
      await api.post(`/tenancies/${this.id}/payments`, { amount, method });
      this.setState({ documents: null, payments: null });
      this._loadBilling();
    } catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.generic') }); }
  }

  _openUtilityForm() {
    this.setState({
      utilityFormOpen: true,
      utilityMeterId: '',
      utilityRate: '',
      utilityBaseFee: '',
      utilityDescription: '',
    });
    if (this.state.meters === null) this._loadMeters();
  }

  _closeUtilityForm() {
    this.setState({
      utilityFormOpen: false,
      utilityMeterId: '',
      utilityRate: '',
      utilityBaseFee: '',
      utilityDescription: '',
    });
  }

  async _loadMeters() {
    try {
      const res = await api.get(`/tenancies/${this.id}/meters`);
      this.setState({ meters: res.data || [] });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.errors.load_meters') });
    }
  }

  _maintenanceHtml(c, isLandlord) {
    const list = this.state.maintenance;
    const cats = ['PLUMBING', 'ELECTRICAL', 'HEATING', 'APPLIANCE', 'STRUCTURAL', 'PEST', 'OTHER'];
    const prios = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
    const units = Array.isArray(c.units) ? c.units : [];
    const form = this.state.maintenanceFormOpen ? `
      <form class="card stack" data-mt-create>
        <h3>${esc(t('tenancy.maintenance.new'))}</h3>
        <div class="field">
          <label>${esc(t('tenancy.maintenance.field_title'))}</label>
          <input class="input" name="title" type="text" maxlength="190" required />
        </div>
        <div class="field">
          <label>${esc(t('tenancy.maintenance.field_description'))}</label>
          <textarea class="input" name="description" rows="3"></textarea>
        </div>
        <div class="row" style="gap:var(--space-3);flex-wrap:wrap">
          <div class="field grow">
            <label>${esc(t('tenancy.maintenance.field_category'))}</label>
            <select class="select" name="category">${cats.map((k) => `<option value="${k}">${esc(t('tenancy.maintenance.category.' + k))}</option>`).join('')}</select>
          </div>
          <div class="field grow">
            <label>${esc(t('tenancy.maintenance.field_priority'))}</label>
            <select class="select" name="priority">${prios.map((k) => `<option value="${k}" ${k === 'NORMAL' ? 'selected' : ''}>${esc(t('tenancy.maintenance.priority.' + k))}</option>`).join('')}</select>
          </div>
          ${units.length ? `
          <div class="field grow">
            <label>${esc(t('tenancy.maintenance.field_unit'))}</label>
            <select class="select" name="unit_id"><option value="">${esc(t('tenancy.maintenance.field_unit_none'))}</option>${units.map((u) => `<option value="${esc(u.unit_id)}">${esc(u.identifier)}</option>`).join('')}</select>
          </div>` : ''}
        </div>
        <div class="field">
          <label>${esc(t('tenancy.maintenance.photos.title'))}</label>
          <input class="input" type="file" name="photos" accept="image/*" multiple />
          <span class="faint">${esc(t('tenancy.maintenance.photos.hint'))}</span>
        </div>
        <div class="row" style="justify-content:space-between">
          <button type="button" class="btn btn-ghost" data-mt-action="new-close">${esc(t('tenancy.maintenance.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.maintenanceBusy ? 'disabled' : ''}>${esc(t('tenancy.maintenance.submit'))}</button>
        </div>
      </form>
    ` : `<div class="row"><button class="btn btn-primary" data-mt-action="new-open">${icon('plus', { size: 'sm' })}<span>${esc(t('tenancy.maintenance.new'))}</span></button></div>`;

    let body;
    if (list === null) body = `<div class="muted">${esc(t('tenancy.maintenance.loading'))}</div>`;
    else if (list.length === 0) body = `<div class="empty">${esc(t('tenancy.maintenance.empty'))}</div>`;
    else body = `<div class="stack">${list.map((r) => this._maintenanceCardHtml(r, isLandlord)).join('')}</div>`;
    return `<div class="stack">${form}${body}</div>`;
  }

  _maintenanceStatusClass(status) {
    if (status === 'RESOLVED') return 'badge-success';
    if (status === 'CANCELLED') return 'badge-muted';
    if (status === 'OPEN') return 'badge-warning';
    return 'badge';
  }

  _maintenanceCardHtml(r, isLandlord) {
    const expanded = this.state.maintenanceExpandedId === r.id;
    const cls = this._maintenanceStatusClass(r.status);
    return `
      <div class="card stack">
        <div class="row" style="gap:var(--space-2);align-items:center">
          <span class="badge ${cls}">${esc(t('tenancy.maintenance.status.' + r.status))}</span>
          <strong class="grow">${esc(r.title)}</strong>
          <button class="btn btn-ghost btn-sm" data-mt-action="expand" data-mt-id="${esc(r.id)}">${esc(expanded ? t('tenancy.maintenance.collapse') : t('tenancy.maintenance.expand'))}</button>
        </div>
        <span class="faint">${esc(t('tenancy.maintenance.category.' + r.category))} · ${esc(t('tenancy.maintenance.priority.' + r.priority))}${r.unit_identifier ? ` · ${esc(r.unit_identifier)}` : ''}</span>
        <span class="faint">${esc(t('tenancy.maintenance.reported_by', { name: r.reporter_name || r.reporter_email || '' }))} · ${esc(formatDateTime(r.created_at))}</span>
        ${expanded ? this._maintenanceDetailHtml(r, isLandlord) : ''}
      </div>`;
  }

  _maintenanceDetailHtml(r, isLandlord) {
    if (this.state.maintenanceDetailBusy || !this.state.maintenanceDetail || this.state.maintenanceDetail.id !== r.id) {
      return `<div class="muted">${esc(t('tenancy.maintenance.loading'))}</div>`;
    }
    const d = this.state.maintenanceDetail;
    const comments = Array.isArray(d.comments) ? d.comments : [];
    const terminal = d.status === 'RESOLVED' || d.status === 'CANCELLED';
    const statusBtns = (isLandlord && !terminal) ? `
      <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
        <span class="faint">${esc(t('tenancy.maintenance.set_status'))}:</span>
        ${['IN_PROGRESS', 'RESOLVED', 'CANCELLED'].filter((s) => s !== d.status).map((s) => `<button class="btn btn-sm btn-ghost" data-mt-action="status" data-mt-id="${esc(d.id)}" data-mt-status="${s}">${esc(t('tenancy.maintenance.status.' + s))}</button>`).join('')}
      </div>` : '';
    const thread = comments.length
      ? comments.map((cm) => `<div class="stack-2" style="border-top:1px solid var(--border);padding-top:var(--space-2)"><span class="faint">${esc(cm.author_name || cm.author_email || '')} · ${esc(formatDateTime(cm.created_at))}</span><div>${esc(cm.body)}</div></div>`).join('')
      : `<div class="muted">${esc(t('tenancy.maintenance.no_comments'))}</div>`;
    return `
      <div class="stack" style="border-top:1px solid var(--border);padding-top:var(--space-3)">
        ${d.description ? `<div>${esc(d.description)}</div>` : ''}
        ${statusBtns}
        ${this._maintenancePhotosHtml(d, isLandlord, terminal)}
        <h4 style="margin:0">${esc(t('tenancy.maintenance.comments_title'))}</h4>
        <div class="stack">${thread}</div>
        <form class="row" style="gap:var(--space-2)" data-mt-comment="${esc(d.id)}">
          <textarea class="input grow" name="body" rows="2" placeholder="${esc(t('tenancy.maintenance.comment_placeholder'))}"></textarea>
          <button type="submit" class="btn btn-primary">${esc(t('tenancy.maintenance.comment_send'))}</button>
        </form>
      </div>`;
  }

  _maintenancePhotosHtml(d, isLandlord, terminal) {
    const photos = Array.isArray(d.photos) ? d.photos : [];
    if (photos.length === 0 && terminal) return '';
    const tiles = photos.map((p) => {
      const canDelete = !terminal && (isLandlord || (auth.user && p.uploader_user_id === auth.user.id));
      return `
      <div class="room-photo-tile">
        <button type="button" class="defect-photo-trigger" data-mt-action="photo-view" aria-label="${esc(t('tenancy.maintenance.photos.view'))}" title="${esc(t('tenancy.maintenance.photos.view'))}">
          <img alt="${esc(t('tenancy.maintenance.photos.photo'))}" data-protected-src="/maintenance-photos/${esc(p.id)}/photo" />
          <span class="defect-photo-trigger-overlay">${icon('eye', { size: 'sm' })}</span>
        </button>
        ${canDelete ? `<button type="button" class="btn btn-ghost btn-icon room-photo-delete" data-mt-action="photo-delete" data-mt-photo="${esc(p.id)}" aria-label="${esc(t('common.delete'))}" title="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
      </div>`;
    }).join('');
    const adder = !terminal ? `
      <label class="room-photo-add" title="${esc(t('tenancy.maintenance.photos.add'))}">
        ${icon('upload', { size: 'sm' })}
        <span>${esc(t('tenancy.maintenance.photos.add'))}</span>
        <input type="file" accept="image/*" hidden data-mt-photo-input="${esc(d.id)}" />
      </label>` : '';
    return `
      <div class="room-photos">
        <div class="room-photos-head">
          <strong>${esc(t('tenancy.maintenance.photos.title'))}</strong>
          <span class="faint">${esc(t('tenancy.maintenance.photos.hint'))}</span>
        </div>
        <div class="room-photo-grid">${tiles}${adder}</div>
      </div>`;
  }

  _wireMaintenance() {
    this.el.querySelectorAll('[data-mt-action]').forEach((b) => b.addEventListener('click', () => {
      const action = b.getAttribute('data-mt-action');
      if (action === 'new-open') this.setState({ maintenanceFormOpen: true });
      else if (action === 'new-close') this.setState({ maintenanceFormOpen: false });
      else if (action === 'expand') this._toggleMaintenance(b.getAttribute('data-mt-id'));
      else if (action === 'status') this._changeMaintenanceStatus(b.getAttribute('data-mt-id'), b.getAttribute('data-mt-status'));
      else if (action === 'photo-view') this._viewMaintenancePhoto(b);
      else if (action === 'photo-delete') this._deleteMaintenancePhoto(b.getAttribute('data-mt-photo'));
    }));
    const createForm = this.el.querySelector('[data-mt-create]');
    if (createForm) createForm.addEventListener('submit', (e) => { e.preventDefault(); this._createMaintenance(createForm); });
    const commentForm = this.el.querySelector('[data-mt-comment]');
    if (commentForm) commentForm.addEventListener('submit', (e) => { e.preventDefault(); this._addMaintenanceComment(commentForm.getAttribute('data-mt-comment'), commentForm); });
    const photoInput = this.el.querySelector('[data-mt-photo-input]');
    if (photoInput) photoInput.addEventListener('change', () => {
      const file = photoInput.files && photoInput.files[0];
      if (file) this._uploadMaintenancePhoto(photoInput.getAttribute('data-mt-photo-input'), file);
    });
  }

  async _loadMaintenance() {
    try {
      const res = await api.get(`/tenancies/${this.id}/maintenance`);
      this.setState({ maintenance: res.data || [] });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.load') });
    }
  }

  async _toggleMaintenance(id) {
    if (this.state.maintenanceExpandedId === id) {
      this.setState({ maintenanceExpandedId: null, maintenanceDetail: null });
      return;
    }
    this.setState({ maintenanceExpandedId: id, maintenanceDetail: null, maintenanceDetailBusy: true });
    try {
      const detail = await api.get(`/maintenance/${id}`);
      if (this.state.maintenanceExpandedId === id) this.setState({ maintenanceDetail: detail, maintenanceDetailBusy: false });
    } catch (err) {
      this.setState({ maintenanceDetailBusy: false, error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.load') });
    }
  }

  async _createMaintenance(form) {
    if (this.state.maintenanceBusy) return;
    const title = (form.querySelector('[name=title]')?.value || '').trim();
    if (!title) { this.setState({ error: t('tenancy.maintenance.errors.save') }); return; }
    const data = {
      title,
      description: (form.querySelector('[name=description]')?.value || '').trim(),
      category: form.querySelector('[name=category]')?.value || 'OTHER',
      priority: form.querySelector('[name=priority]')?.value || 'NORMAL',
    };
    const unitSel = form.querySelector('[name=unit_id]');
    if (unitSel && unitSel.value) data.unit_id = unitSel.value;
    const files = Array.from(form.querySelector('[name=photos]')?.files || []);
    this.setState({ maintenanceBusy: true });
    try {
      const created = await api.post(`/tenancies/${this.id}/maintenance`, data);
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('photo', file);
          await api.post(`/maintenance/${created.id}/photos`, fd);
        } catch (_) {
          toast.error(t('tenancy.maintenance.errors.photo_upload'));
        }
      }
      this.setState({ maintenanceBusy: false, maintenanceFormOpen: false, maintenance: null, error: null });
      this._loadMaintenance();
    } catch (err) {
      this.setState({ maintenanceBusy: false, error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.save') });
    }
  }

  async _changeMaintenanceStatus(id, status) {
    try {
      const detail = await api.post(`/maintenance/${id}/status`, { status });
      this.setState({
        maintenance: null,
        maintenanceDetail: this.state.maintenanceExpandedId === id ? detail : this.state.maintenanceDetail,
        error: null,
      });
      this._loadMaintenance();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.status') });
    }
  }

  async _addMaintenanceComment(id, form) {
    const body = (form.querySelector('[name=body]')?.value || '').trim();
    if (!body) return;
    try {
      const detail = await api.post(`/maintenance/${id}/comments`, { body });
      this.setState({ maintenance: null, maintenanceDetail: detail, error: null });
      this._loadMaintenance();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.comment') });
    }
  }

  async _uploadMaintenancePhoto(requestId, file) {
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const detail = await api.post(`/maintenance/${requestId}/photos`, fd);
      if (this.state.maintenanceExpandedId === requestId) this.setState({ maintenanceDetail: detail, error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.photo_upload') });
    }
  }

  async _deleteMaintenancePhoto(photoId) {
    if (!window.confirm(t('tenancy.maintenance.photos.delete_confirm'))) return;
    try {
      const detail = await api.delete(`/maintenance-photos/${photoId}`);
      if (this.state.maintenanceExpandedId === detail.id) this.setState({ maintenanceDetail: detail, error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.maintenance.errors.photo_delete') });
    }
  }

  _viewMaintenancePhoto(trigger) {
    const img = trigger.querySelector('img');
    const url = img && img.src && img.src.startsWith('blob:') ? img.src : null;
    if (!url) return;
    showModal({
      title: t('tenancy.maintenance.photos.photo'),
      body: `<img src="${esc(url)}" alt="${esc(t('tenancy.maintenance.photos.photo'))}" class="lightbox-image" />`,
      actions: [{ label: t('common.close'), variant: 'primary' }],
    });
  }

  _selectUtilityMeter(id) {
    const meter = (this.state.meters || []).find((m) => m.id === id);
    let desc = '';
    let rate = '';
    let baseFee = '';
    if (meter) {
      const r = meter.kind === 'fee' ? null : this._latestAcceptedReading(meter);
      const tag = meter.label ? `${meter.kind} (${meter.label})` : meter.kind;
      if (r && r.delta !== null && r.delta !== undefined) {
        desc = `${meter.unit_label} — ${tag}: ${r.previous_value}→${r.reading_value} ${meter.unit_of_measure}`;
      } else {
        desc = `${meter.unit_label} — ${tag}`;
      }
      if (meter.unit_price != null && meter.unit_price !== '') {
        rate = String(Number(meter.unit_price) / 100);
      }
      if (meter.monthly_fee != null && meter.monthly_fee !== '') {
        baseFee = String(Number(meter.monthly_fee) / 100);
      }
    }
    this.setState({ utilityMeterId: id, utilityDescription: desc, utilityRate: rate, utilityBaseFee: baseFee });
  }

  async _saveUtilityProforma() {
    const meter = (this.state.meters || []).find((m) => m.id === this.state.utilityMeterId);
    if (!meter) { this.setState({ error: t('tenancy.utility_form.errors.pick_meter') }); return; }
    const isFee = meter.kind === 'fee';
    const latest = isFee ? null : this._latestAcceptedReading(meter);
    const description = (this.state.utilityDescription || '').trim();
    if (!description) { this.setState({ error: t('tenancy.utility_form.errors.description_required') }); return; }

    const baseFeeNum = parseFloat(this.state.utilityBaseFee);
    const baseFeeMinor = isFinite(baseFeeNum) && baseFeeNum >= 0 ? Math.round(baseFeeNum * 100) : 0;
    const rateNum = parseFloat(this.state.utilityRate);
    const unitPriceMinor = isFinite(rateNum) && rateNum >= 0 ? Math.round(rateNum * 100) : 0;

    const item = { type: isFee ? 'fee' : 'utility', description };
    if (baseFeeMinor > 0) item.base_fee = baseFeeMinor;
    if (!isFee && latest && latest.delta !== null && latest.delta !== undefined && unitPriceMinor > 0) {
      item.meter_reading_id = latest.id;
      item.quantity = latest.delta;
      item.unit_price = unitPriceMinor;
    }
    if (!('base_fee' in item) && !('unit_price' in item)) {
      this.setState({ error: t('tenancy.utility_form.errors.set_fee_or_meter') });
      return;
    }
    try {
      await api.post(`/tenancies/${this.id}/pending-items`, item);
      this.setState({
        utilityFormOpen: false,
        utilityMeterId: '',
        utilityRate: '',
        utilityBaseFee: '',
        utilityDescription: '',
        pendingItems: null,
      });
      this._loadBilling();
    } catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.utility_form.errors.generic') }); }
  }

  _openReadingForm(meterId) {
    const today = new Date().toISOString().slice(0, 10);
    this.setState({
      meterReadingForms: {
        ...this.state.meterReadingForms,
        [meterId]: { value: '', read_at: today, busy: false },
      },
    });
  }

  _closeReadingForm(meterId) {
    const next = { ...this.state.meterReadingForms };
    delete next[meterId];
    this.setState({ meterReadingForms: next });
  }

  _updateReadingForm(meterId, patch) {
    const cur = this.state.meterReadingForms[meterId] || { value: '', read_at: '', busy: false };
    this.state.meterReadingForms = { ...this.state.meterReadingForms, [meterId]: { ...cur, ...patch } };
  }

  async _saveReading(meterId, photoFile = null) {
    const form = this.state.meterReadingForms[meterId];
    if (!form) return;
    const value = parseFloat(form.value);
    if (!isFinite(value) || value < 0) { this.setState({ error: t('tenancy.meters.errors.value_invalid') }); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.read_at || '')) { this.setState({ error: t('tenancy.meters.errors.date_invalid') }); return; }
    this.setState({
      meterReadingForms: { ...this.state.meterReadingForms, [meterId]: { ...form, busy: true } },
    });
    try {
      const reading = await api.post(`/meters/${meterId}/readings`, {
        reading_value: form.value,
        read_at: form.read_at,
      });
      if (photoFile && reading && reading.id) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        try { await api.post(`/readings/${reading.id}/photo`, fd); }
        catch (e) { /* keep the reading; surface only the upload error */ throw e; }
      }
      const next = { ...this.state.meterReadingForms };
      delete next[meterId];
      this.setState({ meterReadingForms: next, meters: null, error: null });
      this._loadMeters();
    } catch (err) {
      this.setState({
        error: err instanceof ApiError ? err.message : t('tenancy.meters.errors.save'),
        meterReadingForms: { ...this.state.meterReadingForms, [meterId]: { ...form, busy: false } },
      });
    }
  }

  _reviewReading(readingId, action) {
    if (action === 'reject') { this._openRejectModal(readingId); return; }
    this._postReview(readingId, 'accept', null);
  }

  // Send-back dialog: one-tap preset reasons ("need a photo" / "value looks
  // wrong") prefill the note, which stays editable. The note is required —
  // the tenant always learns why the reading was sent back.
  _openRejectModal(readingId) {
    const presets = ['photo', 'value'];
    const body = `
      <div class="stack">
        <p class="faint">${esc(t('tenancy.meters.reject_modal.hint'))}</p>
        <div class="row" style="gap:var(--space-2);flex-wrap:wrap">
          ${presets.map((p) => `<button type="button" class="btn btn-ghost" data-reject-preset="${p}">${esc(t('tenancy.meters.reject_modal.preset_' + p))}</button>`).join('')}
        </div>
        <div class="field">
          <label>${esc(t('tenancy.meters.reject_modal.note_label'))}</label>
          <textarea class="input" data-reject-note rows="3" placeholder="${esc(t('tenancy.meters.reject_modal.note_placeholder'))}"></textarea>
        </div>
      </div>`;
    const m = showModal({
      title: t('tenancy.meters.reject_modal.title'),
      body,
      actions: [
        { label: t('common.cancel') },
        {
          label: t('tenancy.meters.reject_modal.submit'),
          variant: 'danger',
          onClick: async () => {
            const ta = m.el.querySelector('[data-reject-note]');
            const note = (ta && ta.value || '').trim();
            if (!note) { if (ta) ta.focus(); return false; }
            return this._postReview(readingId, 'reject', note);
          },
        },
      ],
    });
    m.el.querySelectorAll('[data-reject-preset]').forEach((b) => b.addEventListener('click', () => {
      const ta = m.el.querySelector('[data-reject-note]');
      if (!ta) return;
      ta.value = t('tenancy.meters.reject_modal.preset_' + b.getAttribute('data-reject-preset') + '_text');
      ta.focus();
    }));
  }

  async _postReview(readingId, action, note) {
    try {
      await api.post(`/readings/${readingId}/${action}`, { note });
      this.setState({ meters: null, error: null });
      this._loadMeters();
      return true;
    } catch (err) {
      const fallback = action === 'accept' ? 'tenancy.meters.errors.review_accept' : 'tenancy.meters.errors.review_reject';
      this.setState({ error: err instanceof ApiError ? err.message : t(fallback) });
      return false;
    }
  }

  // Tenant fix-up for a sent-back reading: pick a photo, upload it, and the
  // backend resubmits the reading (status back to PENDING) automatically.
  _resubmitReading(readingId) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,application/pdf';
    input.addEventListener('change', async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('photo', file);
      try {
        await api.post(`/readings/${readingId}/photo`, fd);
        toast.success(t('tenancy.meters.resubmitted'));
        this.setState({ meters: null, error: null });
        this._loadMeters();
      } catch (err) {
        this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.meters.errors.save') });
      }
    });
    input.click();
  }

  async _deleteReading(readingId, force = false) {
    if (!force && !confirm(t('tenancy.meters.delete_confirm'))) return;
    try {
      const url = force ? `/readings/${readingId}?force=true` : `/readings/${readingId}`;
      await api.delete(url);
      this.setState({ meters: null, error: null });
      this._loadMeters();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        if (confirm(t('tenancy.meters.delete_proforma_confirm'))) {
          return this._deleteReading(readingId, true);
        }
        return;
      }
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancy.meters.errors.delete') });
    }
  }

  _refreshSameDayWarning(formEl, meterId, readAt) {
    const meter = (this.state.meters || []).find((m) => m.id === meterId);
    if (!meter) return;
    const has = (meter.readings || []).some((r) => r.read_at === readAt);
    let warn = formEl.querySelector('[data-reading-sameday-warning]');
    if (has && !warn) {
      warn = document.createElement('div');
      warn.className = 'faint';
      warn.setAttribute('data-reading-sameday-warning', '');
      const dateField = formEl.querySelector('input[name="read_at"]').closest('.row');
      dateField.insertAdjacentElement('afterend', warn);
    }
    if (warn) {
      warn.style.display = has ? '' : 'none';
      if (has) warn.textContent = t('tenancy.meters.form.same_day_warning', { date: readAt });
    }
  }
}
